import type { LevelDef } from '../core/game';

/**
 * The Hub's own campaign — chapter "The Moon". Spec:
 * docs/superpowers/specs/2026-09-02-the-hub-design.md.
 *
 * Hub levels live outside `LEVELS` on purpose: ids start at 101 so they never
 * collide with the 25 campaign episodes (or the Test Arena's 900), and the
 * campaign's unlock cursor (`hambi.unlocked`) never moves because of them —
 * hub progress is its own key (`hambi.hub`, see ProgressService).
 *
 * Every level here runs the Ember Barrage tower-HP system from the first
 * second (`towerHp: 10`): attackers throw things at towers, passive enemies
 * merely walk, and a tower that hits 0 HP is destroyed. The Moon fields ONLY
 * its own enemies — no Nugget Troopers or other campaign regulars. Levels
 * 3–5 (Revision 4) drop the Bar Waiter and add the Microwaved Nugget, the
 * 1.99 Combo, Chiply the Chip, the Microwave and the boss, Chiply's Dad.
 * Levels 6–7 (spec 2026-09-09-moon-stages-design.md) add Nacho, Seasonez
 * Chippington and The Lifeguard. Levels 8–9 (spec
 * 2026-09-10-nacho-stages-design.md) are the nacho stages: they drop the
 * Nuisance Nugget, the Cosmic Corroder and the 1.99 Combo entirely and field
 * the Moon, Spiced and Laser Nachos instead. Level 10 (spec
 * 2026-09-11-through-space-and-time-design.md) is the chapter FINALE: three
 * entrances, thirty waves, two changes of scene and Prince Nacho.
 */

export const HUB_TOWER_HP = 10;

/**
 * The hub chapters' Test Arena ids, declared HERE rather than imported from
 * testArena (which imports this file) so the chapter table can name them.
 */
export const MOON_ARENA_LEVEL_ID = 901;
export const FUNGI_ARENA_LEVEL_ID = 904;

/** First hub level id. Hub ids are HUB_FIRST_LEVEL_ID + (position in chapter). */
export const HUB_FIRST_LEVEL_ID = 101;

export interface HubChapterDef {
  /** 1-based chapter number, shown as "01". */
  number: number;
  name: string;
  /** Atlas key washed into the chapter card. */
  art: string;
  /**
   * The chapter's accent — card border and stripe, the CHAPTER label, the
   * chapter screen's title, row accents and TEST button: The Moon red, The
   * Fungi Forest orange. Peaceful's green still overrides the rows and title.
   */
  color: number;
  colorHex: string;
  /** The TEST button's resting fill (it lights to `color` under the pointer). */
  testHex: string;
  /** The Test Arena its chapter screen's TEST button opens. */
  arenaId: number;
  levels: LevelDef[];
}

const HAMBI_BAR: LevelDef = {
  id: 101,
  chapter: 1,
  episode: 'I',
  name: 'Hambi Bar',
  subtitle: 'The Moon',
  background: 'bg-hambibar',
  decor: 'hambibar',
  // The bar counter, its wine glasses and a row of stools run along the top
  // (y < 190) and the regulars' tables hug both side walls, so the route
  // enters mid-left below the tables, climbs to just under the stools, and
  // walks four switchbacks across the open plank floor before leaving
  // mid-right.
  path: [
    { x: 0, y: 400 },
    { x: 200, y: 400 },
    { x: 200, y: 230 },
    { x: 400, y: 230 },
    { x: 400, y: 620 },
    { x: 600, y: 620 },
    { x: 600, y: 230 },
    { x: 800, y: 230 },
    { x: 800, y: 620 },
    { x: 960, y: 620 },
    { x: 960, y: 400 },
    { x: 1040, y: 400 },
  ],
  // 16 plots: two by the entrance, three in each of the four corridors between
  // the switchback legs (each covers two passes), and two by the exit. Every
  // pair is >= 120px apart and none comes within 50px of the trail.
  plots: [
    { id: 0, pos: { x: 120, y: 300 } },
    { id: 1, pos: { x: 120, y: 520 } },
    { id: 2, pos: { x: 300, y: 310 } },
    { id: 3, pos: { x: 300, y: 430 } },
    { id: 4, pos: { x: 300, y: 550 } },
    { id: 5, pos: { x: 500, y: 310 } },
    { id: 6, pos: { x: 500, y: 430 } },
    { id: 7, pos: { x: 500, y: 550 } },
    { id: 8, pos: { x: 700, y: 310 } },
    { id: 9, pos: { x: 700, y: 430 } },
    { id: 10, pos: { x: 700, y: 550 } },
    { id: 11, pos: { x: 880, y: 310 } },
    { id: 12, pos: { x: 880, y: 430 } },
    { id: 13, pos: { x: 880, y: 550 } },
    { id: 14, pos: { x: 1010, y: 300 } },
    { id: 15, pos: { x: 1010, y: 520 } },
  ],
  startCash: 1500,
  startLives: 5,
  coinReward: 80,
  expReward: 150,
  towerHp: HUB_TOWER_HP,
  waves: [
    // 1 — a couple of Corroders test the bars.
    { groups: [{ enemyType: 'cosmiccorroder', count: 2, interval: 4, startDelay: 0 }] },
    // 2 — a waiter walks the floor.
    {
      groups: [
        { enemyType: 'barwaiter', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 2, interval: 4, startDelay: 3 },
      ],
    },
    // 3 — Corroders in a line.
    { groups: [{ enemyType: 'cosmiccorroder', count: 4, interval: 3, startDelay: 0 }] },
    // 4 — the first Nuisance Nugget, monocle and all.
    {
      groups: [
        { enemyType: 'nuisancenugget', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 2, interval: 4, startDelay: 4 },
      ],
    },
    // 5 — waiters in pairs.
    {
      groups: [
        { enemyType: 'barwaiter', count: 2, interval: 6, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 3, interval: 3, startDelay: 2 },
      ],
    },
    // 6 — Corroders and a Nugget together.
    {
      groups: [
        { enemyType: 'cosmiccorroder', count: 3, interval: 3, startDelay: 0 },
        { enemyType: 'nuisancenugget', count: 1, interval: 1, startDelay: 6 },
      ],
    },
    // 7 — a waiter wall.
    {
      groups: [
        { enemyType: 'barwaiter', count: 3, interval: 4, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 2, interval: 4, startDelay: 5 },
      ],
    },
    // 8 — two Nuggets, twice the damage.
    {
      groups: [
        { enemyType: 'nuisancenugget', count: 2, interval: 6, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 3, interval: 3, startDelay: 3 },
      ],
    },
    // 9 — Corroder rush.
    { groups: [{ enemyType: 'cosmiccorroder', count: 6, interval: 2, startDelay: 0 }] },
    // 10 — a waiter escorted by Corroders.
    {
      groups: [
        { enemyType: 'barwaiter', count: 2, interval: 5, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 4, interval: 2.5, startDelay: 3 },
      ],
    },
    // 11 — waiters and a Nugget.
    {
      groups: [
        { enemyType: 'barwaiter', count: 2, interval: 6, startDelay: 0 },
        { enemyType: 'nuisancenugget', count: 1, interval: 1, startDelay: 8 },
        { enemyType: 'cosmiccorroder', count: 3, interval: 3, startDelay: 10 },
      ],
    },
    // 12 — Corroders, fast.
    {
      groups: [
        { enemyType: 'cosmiccorroder', count: 6, interval: 2, startDelay: 0 },
        { enemyType: 'nuisancenugget', count: 1, interval: 1, startDelay: 8 },
      ],
    },
    // 13 — three Nuggets.
    {
      groups: [
        { enemyType: 'nuisancenugget', count: 3, interval: 5, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 3, interval: 3, startDelay: 4 },
      ],
    },
    // 14 — the waiters' round.
    {
      groups: [
        { enemyType: 'barwaiter', count: 3, interval: 5, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 5, interval: 2.5, startDelay: 4 },
      ],
    },
    // 15 — Nuggets under waiter cover.
    {
      groups: [
        { enemyType: 'barwaiter', count: 2, interval: 6, startDelay: 0 },
        { enemyType: 'nuisancenugget', count: 3, interval: 5, startDelay: 3 },
      ],
    },
    // 16 — everything the bar has, mixed.
    {
      groups: [
        { enemyType: 'nuisancenugget', count: 2, interval: 6, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 5, interval: 2.5, startDelay: 3 },
        { enemyType: 'barwaiter', count: 3, interval: 5, startDelay: 8 },
      ],
    },
    // 17 — last orders: the whole roster, back to back.
    {
      groups: [
        { enemyType: 'cosmiccorroder', count: 8, interval: 2, startDelay: 0 },
        { enemyType: 'nuisancenugget', count: 4, interval: 5, startDelay: 5 },
        { enemyType: 'barwaiter', count: 4, interval: 5, startDelay: 12 },
        { enemyType: 'cosmiccorroder', count: 6, interval: 2, startDelay: 24 },
      ],
    },
  ],
};

const HTD_EXHIBITION: LevelDef = {
  id: 102,
  chapter: 1,
  episode: 'II',
  name: 'HTD Exhibition',
  subtitle: 'The Moon',
  background: 'bg-htdexhibition',
  decor: 'exhibition',
  // Paintings and the velvet rope run along the top edge (y < 100); the empty
  // display case stands against the BOTTOM wall, centred (x 525–755, y 510+),
  // with its hambi guards drawn in front of it. The route enters top-left
  // under the rope, walks four switchbacks over the marble — the middle two
  // stop short of the case — and leaves bottom-right, past it.
  path: [
    { x: 0, y: 160 },
    { x: 240, y: 160 },
    { x: 240, y: 560 },
    { x: 420, y: 560 },
    { x: 420, y: 160 },
    { x: 600, y: 160 },
    { x: 600, y: 440 },
    { x: 780, y: 440 },
    { x: 780, y: 160 },
    { x: 940, y: 160 },
    { x: 940, y: 620 },
    { x: 1040, y: 620 },
  ],
  // 15 plots: a column of four inside the entrance, then the corridors
  // between the switchback legs, thinning out above the case. Every pair is
  // >= 120px apart and none comes within 60px of the trail.
  plots: [
    { id: 0, pos: { x: 120, y: 260 } },
    { id: 1, pos: { x: 120, y: 380 } },
    { id: 2, pos: { x: 120, y: 500 } },
    { id: 3, pos: { x: 120, y: 620 } },
    { id: 4, pos: { x: 330, y: 260 } },
    { id: 5, pos: { x: 330, y: 380 } },
    { id: 6, pos: { x: 330, y: 500 } },
    { id: 7, pos: { x: 510, y: 260 } },
    { id: 8, pos: { x: 510, y: 380 } },
    { id: 9, pos: { x: 690, y: 260 } },
    { id: 10, pos: { x: 690, y: 380 } },
    { id: 11, pos: { x: 860, y: 260 } },
    { id: 12, pos: { x: 860, y: 380 } },
    { id: 13, pos: { x: 860, y: 500 } },
    { id: 14, pos: { x: 860, y: 620 } },
  ],
  startCash: 1800,
  startLives: 5,
  coinReward: 125,
  expReward: 200,
  towerHp: HUB_TOWER_HP,
  waves: [
    // 1 — Corroders on the marble.
    { groups: [{ enemyType: 'cosmiccorroder', count: 3, interval: 3, startDelay: 0 }] },
    // 2 — a Nugget in a top hat admires the paintings.
    {
      groups: [
        { enemyType: 'nuisancenugget', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 2, interval: 4, startDelay: 3 },
      ],
    },
    // 3 — waiters wander in from the bar next door.
    {
      groups: [
        { enemyType: 'barwaiter', count: 2, interval: 6, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 2, interval: 4, startDelay: 3 },
      ],
    },
    // 4 — Corroders in numbers.
    { groups: [{ enemyType: 'cosmiccorroder', count: 5, interval: 2.5, startDelay: 0 }] },
    // 5 — Corroders and a Nugget.
    {
      groups: [
        { enemyType: 'cosmiccorroder', count: 4, interval: 2.5, startDelay: 0 },
        { enemyType: 'nuisancenugget', count: 1, interval: 1, startDelay: 5 },
      ],
    },
    // 6 — a waiter wall.
    {
      groups: [
        { enemyType: 'barwaiter', count: 3, interval: 4, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 3, interval: 3, startDelay: 4 },
      ],
    },
    // 7 — two Nuggets.
    {
      groups: [
        { enemyType: 'nuisancenugget', count: 2, interval: 6, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 3, interval: 3, startDelay: 3 },
      ],
    },
    // 8 — Corroder rush.
    { groups: [{ enemyType: 'cosmiccorroder', count: 7, interval: 2, startDelay: 0 }] },
    // 9 — the waiters' round.
    {
      groups: [
        { enemyType: 'barwaiter', count: 3, interval: 5, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 4, interval: 2.5, startDelay: 4 },
      ],
    },
    // 10 — Nuggets and waiters.
    {
      groups: [
        { enemyType: 'nuisancenugget', count: 2, interval: 6, startDelay: 0 },
        { enemyType: 'barwaiter', count: 2, interval: 6, startDelay: 3 },
      ],
    },
    // 11 — a fast mixed line.
    {
      groups: [
        { enemyType: 'cosmiccorroder', count: 6, interval: 2, startDelay: 0 },
        { enemyType: 'nuisancenugget', count: 1, interval: 1, startDelay: 8 },
        { enemyType: 'barwaiter', count: 2, interval: 6, startDelay: 10 },
      ],
    },
    // 12 — three Nuggets under waiter cover.
    {
      groups: [
        { enemyType: 'barwaiter', count: 2, interval: 6, startDelay: 0 },
        { enemyType: 'nuisancenugget', count: 3, interval: 5, startDelay: 3 },
      ],
    },
    // 13 — Corroders again, thicker.
    {
      groups: [
        { enemyType: 'cosmiccorroder', count: 8, interval: 2, startDelay: 0 },
        { enemyType: 'barwaiter', count: 2, interval: 6, startDelay: 6 },
      ],
    },
    // 14 — THE BURNT FRIES. The only wave they ever appear in: a slow, charred
    // wall of HP with Corroders blowing leaves from behind it.
    {
      groups: [
        { enemyType: 'burntfry', count: 4, interval: 5, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 4, interval: 2.5, startDelay: 6 },
      ],
    },
    // 15 — Corroders and Nuggets, the barrage at full strength.
    {
      groups: [
        { enemyType: 'cosmiccorroder', count: 6, interval: 2, startDelay: 0 },
        { enemyType: 'nuisancenugget', count: 3, interval: 5, startDelay: 6 },
      ],
    },
    // 16 — everything the exhibition has, mixed.
    {
      groups: [
        { enemyType: 'barwaiter', count: 3, interval: 5, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 5, interval: 2.5, startDelay: 4 },
        { enemyType: 'nuisancenugget', count: 2, interval: 6, startDelay: 10 },
      ],
    },
    // 17 — closing time: the full roster back to back. Winning this for the
    // first time earns the Diamond.
    {
      groups: [
        { enemyType: 'cosmiccorroder', count: 8, interval: 2, startDelay: 0 },
        { enemyType: 'nuisancenugget', count: 4, interval: 5, startDelay: 5 },
        { enemyType: 'barwaiter', count: 4, interval: 5, startDelay: 12 },
        { enemyType: 'cosmiccorroder', count: 6, interval: 2, startDelay: 24 },
      ],
    },
  ],
};

const COSMIC_QUARTERS: LevelDef = {
  id: 103,
  chapter: 1,
  episode: 'III',
  name: 'Cosmic Quarters',
  subtitle: 'The Moon',
  background: 'bg-cosmicquarters',
  // A floating platform in the sky: bg-cosmicquarters paints its deck from
  // x≈100–1180, y≈95–630 with white and purple skyscrapers on the rim (the
  // bottom row of towers pokes up to y≈600 at x 260–420 and 830–1100). The
  // route enters mid-left, climbs to just under the top rim and walks five
  // switchbacks across the deck, staying above the bottom towers.
  path: [
    { x: 0, y: 340 },
    { x: 160, y: 340 },
    { x: 160, y: 140 },
    { x: 330, y: 140 },
    { x: 330, y: 570 },
    { x: 500, y: 570 },
    { x: 500, y: 140 },
    { x: 670, y: 140 },
    { x: 670, y: 570 },
    { x: 840, y: 570 },
    { x: 840, y: 140 },
    { x: 980, y: 140 },
    { x: 980, y: 340 },
    { x: 1040, y: 340 },
  ],
  // 15 plots: three down each of the five corridors between the switchback
  // legs (each covers two passes). Every pair is >= 100px apart and none
  // comes within 70px of the trail.
  plots: [
    { id: 0, pos: { x: 245, y: 250 } },
    { id: 1, pos: { x: 245, y: 355 } },
    { id: 2, pos: { x: 245, y: 460 } },
    { id: 3, pos: { x: 415, y: 250 } },
    { id: 4, pos: { x: 415, y: 355 } },
    { id: 5, pos: { x: 415, y: 460 } },
    { id: 6, pos: { x: 585, y: 250 } },
    { id: 7, pos: { x: 585, y: 355 } },
    { id: 8, pos: { x: 585, y: 460 } },
    { id: 9, pos: { x: 755, y: 250 } },
    { id: 10, pos: { x: 755, y: 355 } },
    { id: 11, pos: { x: 755, y: 460 } },
    { id: 12, pos: { x: 910, y: 250 } },
    { id: 13, pos: { x: 910, y: 355 } },
    { id: 14, pos: { x: 910, y: 460 } },
  ],
  startCash: 2000,
  startLives: 5,
  coinReward: 150,
  expReward: 250,
  towerHp: HUB_TOWER_HP,
  // No Bar Waiter from here on. The 1.99 Combo arrives on wave 7, the
  // Microwaved Nugget on wave 11.
  waves: [
    // 1 — Corroders on the deck.
    { groups: [{ enemyType: 'cosmiccorroder', count: 3, interval: 3, startDelay: 0 }] },
    // 2 — a Nugget in a top hat.
    {
      groups: [
        { enemyType: 'nuisancenugget', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 2, interval: 4, startDelay: 3 },
      ],
    },
    // 3 — Corroders in a line.
    { groups: [{ enemyType: 'cosmiccorroder', count: 5, interval: 2.5, startDelay: 0 }] },
    // 4 — two Nuggets.
    {
      groups: [
        { enemyType: 'nuisancenugget', count: 2, interval: 6, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 3, interval: 3, startDelay: 3 },
      ],
    },
    // 5 — Corroder rush.
    { groups: [{ enemyType: 'cosmiccorroder', count: 6, interval: 2, startDelay: 0 }] },
    // 6 — Nuggets under Corroder cover.
    {
      groups: [
        { enemyType: 'nuisancenugget', count: 2, interval: 6, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 4, interval: 2.5, startDelay: 3 },
      ],
    },
    // 7 — THE 1.99 COMBO: two nuggets in a box walk out for the first time.
    {
      groups: [
        { enemyType: 'combo199', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 3, interval: 3, startDelay: 4 },
      ],
    },
    // 8 — two Combos and a Nugget.
    {
      groups: [
        { enemyType: 'combo199', count: 2, interval: 6, startDelay: 0 },
        { enemyType: 'nuisancenugget', count: 1, interval: 1, startDelay: 8 },
      ],
    },
    // 9 — Corroders thick, a Combo behind.
    {
      groups: [
        { enemyType: 'cosmiccorroder', count: 7, interval: 2, startDelay: 0 },
        { enemyType: 'combo199', count: 1, interval: 1, startDelay: 10 },
      ],
    },
    // 10 — Nuggets and Combos.
    {
      groups: [
        { enemyType: 'nuisancenugget', count: 3, interval: 5, startDelay: 0 },
        { enemyType: 'combo199', count: 2, interval: 6, startDelay: 4 },
      ],
    },
    // 11 — THE MICROWAVED NUGGET: hot from the oven, the first pair.
    {
      groups: [
        { enemyType: 'microwavednugget', count: 2, interval: 5, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 3, interval: 3, startDelay: 4 },
      ],
    },
    // 12 — three hot Nuggets, two Combos.
    {
      groups: [
        { enemyType: 'microwavednugget', count: 3, interval: 4, startDelay: 0 },
        { enemyType: 'combo199', count: 2, interval: 6, startDelay: 6 },
      ],
    },
    // 13 — the whole quarters, mixed.
    {
      groups: [
        { enemyType: 'nuisancenugget', count: 3, interval: 5, startDelay: 0 },
        { enemyType: 'microwavednugget', count: 2, interval: 5, startDelay: 3 },
        { enemyType: 'cosmiccorroder', count: 4, interval: 2.5, startDelay: 8 },
      ],
    },
    // 14 — Combos in front, hot Nuggets behind.
    {
      groups: [
        { enemyType: 'combo199', count: 3, interval: 5, startDelay: 0 },
        { enemyType: 'microwavednugget', count: 3, interval: 4, startDelay: 6 },
      ],
    },
    // 15 — hot Nugget rush.
    {
      groups: [
        { enemyType: 'microwavednugget', count: 5, interval: 3, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 5, interval: 2.5, startDelay: 5 },
      ],
    },
    // 16 — Nuggets of every kind and Combos.
    {
      groups: [
        { enemyType: 'nuisancenugget', count: 4, interval: 5, startDelay: 0 },
        { enemyType: 'combo199', count: 3, interval: 6, startDelay: 4 },
        { enemyType: 'microwavednugget', count: 2, interval: 5, startDelay: 12 },
      ],
    },
    // 17 — the pressure builds.
    {
      groups: [
        { enemyType: 'microwavednugget', count: 6, interval: 3, startDelay: 0 },
        { enemyType: 'combo199', count: 3, interval: 6, startDelay: 6 },
        { enemyType: 'cosmiccorroder', count: 6, interval: 2, startDelay: 14 },
      ],
    },
    // 18 — lights out: the full roster back to back.
    {
      groups: [
        { enemyType: 'cosmiccorroder', count: 8, interval: 2, startDelay: 0 },
        { enemyType: 'nuisancenugget', count: 4, interval: 5, startDelay: 5 },
        { enemyType: 'combo199', count: 4, interval: 6, startDelay: 12 },
        { enemyType: 'microwavednugget', count: 6, interval: 3, startDelay: 22 },
      ],
    },
  ],
};

const GAZING_DOME: LevelDef = {
  id: 104,
  chapter: 1,
  episode: 'IV',
  name: 'Gazing Dome',
  subtitle: 'The Moon',
  background: 'bg-gazingdome',
  // A glass dome: bg-gazingdome shows the night sky (Earth, moons, a ringed
  // planet, constellations) through the glass along the top (y < 150), and
  // shops and cafe tables hug the sides (x < 100, x > 1180) and the bottom
  // (y > 650). The route enters mid-left, climbs to just under the glass and
  // walks five switchbacks over the tiled floor.
  path: [
    { x: 0, y: 400 },
    { x: 180, y: 400 },
    { x: 180, y: 190 },
    { x: 360, y: 190 },
    { x: 360, y: 620 },
    { x: 540, y: 620 },
    { x: 540, y: 190 },
    { x: 720, y: 190 },
    { x: 720, y: 620 },
    { x: 900, y: 620 },
    { x: 900, y: 190 },
    { x: 1000, y: 190 },
    { x: 1000, y: 400 },
    { x: 1040, y: 400 },
  ],
  // 14 plots: three down each of the four wide corridors and two in the
  // narrow one before the exit. Every pair is >= 100px apart and none comes
  // within 50px of the trail.
  plots: [
    { id: 0, pos: { x: 270, y: 300 } },
    { id: 1, pos: { x: 270, y: 405 } },
    { id: 2, pos: { x: 270, y: 510 } },
    { id: 3, pos: { x: 450, y: 300 } },
    { id: 4, pos: { x: 450, y: 405 } },
    { id: 5, pos: { x: 450, y: 510 } },
    { id: 6, pos: { x: 630, y: 300 } },
    { id: 7, pos: { x: 630, y: 405 } },
    { id: 8, pos: { x: 630, y: 510 } },
    { id: 9, pos: { x: 810, y: 300 } },
    { id: 10, pos: { x: 810, y: 405 } },
    { id: 11, pos: { x: 810, y: 510 } },
    { id: 12, pos: { x: 950, y: 300 } },
    { id: 13, pos: { x: 950, y: 510 } },
  ],
  startCash: 2200,
  startLives: 5,
  coinReward: 175,
  expReward: 300,
  towerHp: HUB_TOWER_HP,
  // Chiply the Chip walks out ONCE per run, on one wave rolled from 13 to 19
  // (the sim's seeded Rng — see LevelDef.roamer), and he goes first: the
  // wave's own groups wait four seconds behind him. A leak of his takes all
  // five lives. Nobody knows which wave until he is on the glass.
  roamer: { enemyType: 'chiply', firstWave: 13, lastWave: 19, leadGap: 4 },
  notice: { art: 'tower-wizard-1', sender: 'Wizard Hambi', text: 'Beware of that chip!' },
  waves: [
    // 1 — Corroders under glass.
    { groups: [{ enemyType: 'cosmiccorroder', count: 3, interval: 3, startDelay: 0 }] },
    // 2 — a Nugget admires the view.
    {
      groups: [
        { enemyType: 'nuisancenugget', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 3, interval: 3, startDelay: 3 },
      ],
    },
    // 3 — a Combo and Corroders.
    {
      groups: [
        { enemyType: 'combo199', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 3, interval: 3, startDelay: 4 },
      ],
    },
    // 4 — hot Nuggets.
    {
      groups: [
        { enemyType: 'microwavednugget', count: 2, interval: 5, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 3, interval: 3, startDelay: 4 },
      ],
    },
    // 5 — Nuggets and a Combo.
    {
      groups: [
        { enemyType: 'nuisancenugget', count: 2, interval: 6, startDelay: 0 },
        { enemyType: 'combo199', count: 1, interval: 1, startDelay: 8 },
      ],
    },
    // 6 — Corroder rush, hot Nuggets behind.
    {
      groups: [
        { enemyType: 'cosmiccorroder', count: 6, interval: 2, startDelay: 0 },
        { enemyType: 'microwavednugget', count: 2, interval: 5, startDelay: 8 },
      ],
    },
    // 7 — Combos and Nuggets.
    {
      groups: [
        { enemyType: 'combo199', count: 2, interval: 6, startDelay: 0 },
        { enemyType: 'nuisancenugget', count: 2, interval: 6, startDelay: 3 },
      ],
    },
    // 8 — four hot Nuggets.
    {
      groups: [
        { enemyType: 'microwavednugget', count: 4, interval: 4, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 3, interval: 3, startDelay: 5 },
      ],
    },
    // 9 — Nuggets under Combo cover.
    {
      groups: [
        { enemyType: 'nuisancenugget', count: 3, interval: 5, startDelay: 0 },
        { enemyType: 'combo199', count: 2, interval: 6, startDelay: 4 },
      ],
    },
    // 10 — Corroders thick, hot Nuggets behind.
    {
      groups: [
        { enemyType: 'cosmiccorroder', count: 8, interval: 2, startDelay: 0 },
        { enemyType: 'microwavednugget', count: 3, interval: 4, startDelay: 8 },
      ],
    },
    // 11 — Combos and hot Nuggets.
    {
      groups: [
        { enemyType: 'combo199', count: 3, interval: 5, startDelay: 0 },
        { enemyType: 'microwavednugget', count: 3, interval: 4, startDelay: 6 },
      ],
    },
    // 12 — Nuggets and Corroders.
    {
      groups: [
        { enemyType: 'nuisancenugget', count: 4, interval: 5, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 5, interval: 2.5, startDelay: 4 },
      ],
    },
    // 13 — hot Nugget rush, Combos behind.
    {
      groups: [
        { enemyType: 'microwavednugget', count: 5, interval: 3, startDelay: 0 },
        { enemyType: 'combo199', count: 3, interval: 6, startDelay: 8 },
      ],
    },
    // 14 — Corroders (Chiply may lead any wave from 13 on).
    {
      groups: [{ enemyType: 'cosmiccorroder', count: 4, interval: 2.5, startDelay: 0 }],
    },
    // 15 — Nuggets of every kind.
    {
      groups: [
        { enemyType: 'nuisancenugget', count: 4, interval: 5, startDelay: 0 },
        { enemyType: 'microwavednugget', count: 4, interval: 4, startDelay: 3 },
        { enemyType: 'combo199', count: 2, interval: 6, startDelay: 12 },
      ],
    },
    // 16 — hot Nuggets and Corroders.
    {
      groups: [
        { enemyType: 'microwavednugget', count: 4, interval: 4, startDelay: 4 },
        { enemyType: 'cosmiccorroder', count: 4, interval: 2.5, startDelay: 10 },
      ],
    },
    // 17 — Combos, Nuggets, hot Nuggets.
    {
      groups: [
        { enemyType: 'combo199', count: 4, interval: 5, startDelay: 0 },
        { enemyType: 'nuisancenugget', count: 4, interval: 5, startDelay: 5 },
        { enemyType: 'microwavednugget', count: 3, interval: 4, startDelay: 14 },
      ],
    },
    // 18 — the dome shakes.
    {
      groups: [
        { enemyType: 'microwavednugget', count: 7, interval: 3, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 8, interval: 2, startDelay: 6 },
        { enemyType: 'combo199', count: 3, interval: 6, startDelay: 16 },
      ],
    },
    // 19 — everything the dome has.
    {
      groups: [
        { enemyType: 'nuisancenugget', count: 5, interval: 5, startDelay: 4 },
        { enemyType: 'combo199', count: 4, interval: 6, startDelay: 10 },
        { enemyType: 'microwavednugget', count: 6, interval: 3, startDelay: 20 },
      ],
    },
  ],
};

const SPACE_STATION: LevelDef = {
  id: 105,
  chapter: 1,
  episode: 'V',
  name: 'The Space Station',
  subtitle: 'The Moon',
  background: 'bg-spacestation',
  trackStyle: 'panel',
  decor: 'station',
  // A satellite seen from above: bg-spacestation's hull plating spans
  // x≈110–1170, y≈80–670, with the solar wings, the dishes and open space on
  // the rim. The route (drawn as a hull walkway so it blends in) enters
  // mid-left, climbs to just under the top seam and walks five switchbacks
  // over the plating — never off the hull. Planets drift past behind it.
  path: [
    { x: 0, y: 360 },
    { x: 170, y: 360 },
    { x: 170, y: 130 },
    { x: 340, y: 130 },
    { x: 340, y: 600 },
    { x: 510, y: 600 },
    { x: 510, y: 130 },
    { x: 680, y: 130 },
    { x: 680, y: 600 },
    { x: 850, y: 600 },
    { x: 850, y: 130 },
    { x: 990, y: 130 },
    { x: 990, y: 360 },
    { x: 1040, y: 360 },
  ],
  // 15 plots: three down each of the five corridors between the switchback
  // legs. Every pair is >= 100px apart and none comes within 70px of the trail.
  plots: [
    { id: 0, pos: { x: 255, y: 240 } },
    { id: 1, pos: { x: 255, y: 365 } },
    { id: 2, pos: { x: 255, y: 490 } },
    { id: 3, pos: { x: 425, y: 240 } },
    { id: 4, pos: { x: 425, y: 365 } },
    { id: 5, pos: { x: 425, y: 490 } },
    { id: 6, pos: { x: 595, y: 240 } },
    { id: 7, pos: { x: 595, y: 365 } },
    { id: 8, pos: { x: 595, y: 490 } },
    { id: 9, pos: { x: 765, y: 240 } },
    { id: 10, pos: { x: 765, y: 365 } },
    { id: 11, pos: { x: 765, y: 490 } },
    { id: 12, pos: { x: 920, y: 240 } },
    { id: 13, pos: { x: 920, y: 365 } },
    { id: 14, pos: { x: 920, y: 490 } },
  ],
  startCash: 2500,
  startLives: 5,
  coinReward: 250,
  expReward: 400,
  towerHp: HUB_TOWER_HP,
  // The Moon's finale rains confetti like The Ember and Flame (Revision 6).
  winConfetti: true,
  notice: {
    art: 'pickup-chip',
    sender: 'Wizard Hambi',
    text: "Chiply's Dad drops a chip every 10 seconds. Press each chip within 5 seconds or you lose two lives!",
  },
  // The Microwave hops out on waves 5, 10 and 15 only; while it lives every
  // Microwaved Nugget carries 1500 HP and throws every second. Chiply's Dad
  // closes the chapter on wave 20, dropping chips to press as he goes.
  waves: [
    // 1 — Corroders on the hull.
    { groups: [{ enemyType: 'cosmiccorroder', count: 3, interval: 3, startDelay: 0 }] },
    // 2 — Nuggets and Corroders.
    {
      groups: [
        { enemyType: 'nuisancenugget', count: 2, interval: 6, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 2, interval: 4, startDelay: 3 },
      ],
    },
    // 3 — Combos.
    {
      groups: [
        { enemyType: 'combo199', count: 2, interval: 6, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 3, interval: 3, startDelay: 3 },
      ],
    },
    // 4 — hot Nuggets.
    {
      groups: [
        { enemyType: 'microwavednugget', count: 3, interval: 4, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 3, interval: 3, startDelay: 5 },
      ],
    },
    // 5 — THE MICROWAVE, with the hot Nuggets it empowers.
    {
      groups: [
        { enemyType: 'microwave', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'microwavednugget', count: 3, interval: 4, startDelay: 3 },
      ],
    },
    // 6 — Nuggets and Combos.
    {
      groups: [
        { enemyType: 'nuisancenugget', count: 3, interval: 5, startDelay: 0 },
        { enemyType: 'combo199', count: 2, interval: 6, startDelay: 4 },
      ],
    },
    // 7 — hot Nuggets and Corroders.
    {
      groups: [
        { enemyType: 'microwavednugget', count: 4, interval: 4, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 5, interval: 2.5, startDelay: 5 },
      ],
    },
    // 8 — Combos and Nuggets.
    {
      groups: [
        { enemyType: 'combo199', count: 3, interval: 5, startDelay: 0 },
        { enemyType: 'nuisancenugget', count: 3, interval: 5, startDelay: 4 },
      ],
    },
    // 9 — hot Nugget rush.
    {
      groups: [
        { enemyType: 'microwavednugget', count: 5, interval: 3, startDelay: 0 },
        { enemyType: 'combo199', count: 2, interval: 6, startDelay: 8 },
      ],
    },
    // 10 — the Microwave again, hot Nuggets and Corroders in tow.
    {
      groups: [
        { enemyType: 'microwave', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'microwavednugget', count: 4, interval: 4, startDelay: 3 },
        { enemyType: 'cosmiccorroder', count: 4, interval: 2.5, startDelay: 10 },
      ],
    },
    // 11 — Nuggets and hot Nuggets.
    {
      groups: [
        { enemyType: 'nuisancenugget', count: 4, interval: 5, startDelay: 0 },
        { enemyType: 'microwavednugget', count: 4, interval: 4, startDelay: 4 },
      ],
    },
    // 12 — Combos and a Corroder wall.
    {
      groups: [
        { enemyType: 'combo199', count: 4, interval: 5, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 8, interval: 2, startDelay: 5 },
      ],
    },
    // 13 — hot Nuggets and Nuggets.
    {
      groups: [
        { enemyType: 'microwavednugget', count: 6, interval: 3, startDelay: 0 },
        { enemyType: 'nuisancenugget', count: 3, interval: 5, startDelay: 6 },
      ],
    },
    // 14 — Combos and hot Nuggets.
    {
      groups: [
        { enemyType: 'combo199', count: 4, interval: 5, startDelay: 0 },
        { enemyType: 'microwavednugget', count: 5, interval: 3, startDelay: 6 },
      ],
    },
    // 15 — the Microwave's last visit, with the biggest hot Nugget escort.
    {
      groups: [
        { enemyType: 'microwave', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'microwavednugget', count: 6, interval: 3, startDelay: 3 },
        { enemyType: 'combo199', count: 2, interval: 6, startDelay: 14 },
      ],
    },
    // 16 — Nuggets of every kind and Corroders.
    {
      groups: [
        { enemyType: 'nuisancenugget', count: 5, interval: 5, startDelay: 0 },
        { enemyType: 'microwavednugget', count: 5, interval: 3, startDelay: 4 },
        { enemyType: 'cosmiccorroder', count: 6, interval: 2, startDelay: 14 },
      ],
    },
    // 17 — Combos and hot Nuggets.
    {
      groups: [
        { enemyType: 'combo199', count: 5, interval: 5, startDelay: 0 },
        { enemyType: 'microwavednugget', count: 6, interval: 3, startDelay: 8 },
      ],
    },
    // 18 — the hull shakes.
    {
      groups: [
        { enemyType: 'microwavednugget', count: 8, interval: 3, startDelay: 0 },
        { enemyType: 'nuisancenugget', count: 4, interval: 5, startDelay: 6 },
        { enemyType: 'cosmiccorroder', count: 8, interval: 2, startDelay: 16 },
      ],
    },
    // 19 — everything but the boss.
    {
      groups: [
        { enemyType: 'combo199', count: 6, interval: 5, startDelay: 0 },
        { enemyType: 'microwavednugget', count: 6, interval: 3, startDelay: 8 },
        { enemyType: 'nuisancenugget', count: 4, interval: 5, startDelay: 20 },
      ],
    },
    // 20 — CHIPLY'S DAD. Every 10 seconds a chip lands on the hull: press it
    // within 5 or lose two lives (K.O). His escort keeps you from massing on him.
    {
      groups: [
        { enemyType: 'chiplysdad', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'microwavednugget', count: 6, interval: 4, startDelay: 8 },
        { enemyType: 'combo199', count: 4, interval: 6, startDelay: 20 },
      ],
    },
  ],
};

const CONSTELLATION_VALLEY: LevelDef = {
  id: 106,
  chapter: 1,
  episode: 'VI',
  name: 'Constellation Valley',
  subtitle: 'The Moon',
  background: 'bg-constellationvalley',
  // A valley floor at night: bg-constellationvalley keeps the constellations
  // in the sky band along the top (y < 150) and the village — shops, crates,
  // houses — around the left, right and bottom rims. The route enters
  // mid-left, climbs to under the sky and walks five switchbacks over the
  // moonlit floor (Cosmic Quarters' layout, re-laid to this art).
  path: [
    { x: 0, y: 400 },
    { x: 170, y: 400 },
    { x: 170, y: 200 },
    { x: 340, y: 200 },
    { x: 340, y: 600 },
    { x: 510, y: 600 },
    { x: 510, y: 200 },
    { x: 680, y: 200 },
    { x: 680, y: 600 },
    { x: 850, y: 600 },
    { x: 850, y: 200 },
    { x: 990, y: 200 },
    { x: 990, y: 400 },
    { x: 1040, y: 400 },
  ],
  // 15 plots: three down each of the five corridors between the switchback
  // legs. Every pair is >= 100px apart and none comes within 70px of the trail.
  plots: [
    { id: 0, pos: { x: 255, y: 300 } },
    { id: 1, pos: { x: 255, y: 400 } },
    { id: 2, pos: { x: 255, y: 500 } },
    { id: 3, pos: { x: 425, y: 300 } },
    { id: 4, pos: { x: 425, y: 400 } },
    { id: 5, pos: { x: 425, y: 500 } },
    { id: 6, pos: { x: 595, y: 300 } },
    { id: 7, pos: { x: 595, y: 400 } },
    { id: 8, pos: { x: 595, y: 500 } },
    { id: 9, pos: { x: 765, y: 300 } },
    { id: 10, pos: { x: 765, y: 400 } },
    { id: 11, pos: { x: 765, y: 500 } },
    { id: 12, pos: { x: 920, y: 300 } },
    { id: 13, pos: { x: 920, y: 400 } },
    { id: 14, pos: { x: 920, y: 500 } },
  ],
  startCash: 2600,
  startLives: 5,
  // The user's figures (spec 2026-09-09 §5): they step down from The Space
  // Station's 250 / 400 on purpose.
  coinReward: 200,
  expReward: 350,
  towerHp: HUB_TOWER_HP,
  // The Moon's roster tuned harder, the Microwave on waves 7 and 15; NACHO
  // (passive, fast, fat) from wave 10 and SEASONEZ CHIPPINGTON (onion rings
  // at towers, two lives on a leak) from wave 13.
  waves: [
    // 1 — Corroders in the valley.
    {
      groups: [{ enemyType: 'cosmiccorroder', count: 4, interval: 2.5, startDelay: 0 }],
    },
    // 2 — Nuggets and Corroders.
    {
      groups: [
        { enemyType: 'nuisancenugget', count: 2, interval: 5, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 3, interval: 3, startDelay: 3 },
      ],
    },
    // 3 — Combos.
    {
      groups: [
        { enemyType: 'combo199', count: 2, interval: 6, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 3, interval: 3, startDelay: 3 },
      ],
    },
    // 4 — hot Nuggets.
    {
      groups: [
        { enemyType: 'microwavednugget', count: 3, interval: 4, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 3, interval: 3, startDelay: 5 },
      ],
    },
    // 5 — Corroder rush.
    {
      groups: [
        { enemyType: 'cosmiccorroder', count: 8, interval: 2, startDelay: 0 },
        { enemyType: 'nuisancenugget', count: 2, interval: 5, startDelay: 8 },
      ],
    },
    // 6 — Combos and hot Nuggets.
    {
      groups: [
        { enemyType: 'combo199', count: 3, interval: 5, startDelay: 0 },
        { enemyType: 'microwavednugget', count: 3, interval: 4, startDelay: 5 },
      ],
    },
    // 7 — THE MICROWAVE, hot Nuggets in tow.
    {
      groups: [
        { enemyType: 'microwave', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'microwavednugget', count: 4, interval: 4, startDelay: 3 },
        { enemyType: 'cosmiccorroder', count: 4, interval: 2.5, startDelay: 10 },
      ],
    },
    // 8 — Nuggets and Combos.
    {
      groups: [
        { enemyType: 'nuisancenugget', count: 4, interval: 5, startDelay: 0 },
        { enemyType: 'combo199', count: 3, interval: 5, startDelay: 4 },
      ],
    },
    // 9 — hot Nugget rush.
    {
      groups: [
        { enemyType: 'microwavednugget', count: 5, interval: 3, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 6, interval: 2, startDelay: 6 },
      ],
    },
    // 10 — NACHO walks out for the first time, very angry, with an escort.
    {
      groups: [
        { enemyType: 'nacho', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 5, interval: 2.5, startDelay: 5 },
        { enemyType: 'nuisancenugget', count: 2, interval: 5, startDelay: 10 },
      ],
    },
    // 11 — Combos and hot Nuggets.
    {
      groups: [
        { enemyType: 'combo199', count: 4, interval: 5, startDelay: 0 },
        { enemyType: 'microwavednugget', count: 4, interval: 4, startDelay: 6 },
      ],
    },
    // 12 — two Nachos.
    {
      groups: [
        { enemyType: 'nacho', count: 2, interval: 8, startDelay: 0 },
        { enemyType: 'microwavednugget', count: 4, interval: 4, startDelay: 4 },
      ],
    },
    // 13 — SEASONEZ CHIPPINGTON: salt, pepper, bucket hat and onion rings.
    {
      groups: [
        { enemyType: 'seasonezchippington', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 6, interval: 2, startDelay: 5 },
        { enemyType: 'combo199', count: 2, interval: 6, startDelay: 12 },
      ],
    },
    // 14 — Nachos, Nuggets, hot Nuggets.
    {
      groups: [
        { enemyType: 'nacho', count: 2, interval: 8, startDelay: 0 },
        { enemyType: 'nuisancenugget', count: 4, interval: 5, startDelay: 3 },
        { enemyType: 'microwavednugget', count: 3, interval: 4, startDelay: 12 },
      ],
    },
    // 15 — the Microwave's second visit, Chippington behind it.
    {
      groups: [
        { enemyType: 'microwave', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'microwavednugget', count: 6, interval: 3, startDelay: 3 },
        { enemyType: 'seasonezchippington', count: 1, interval: 1, startDelay: 14 },
      ],
    },
    // 16 — three Nachos and Combos.
    {
      groups: [
        { enemyType: 'nacho', count: 3, interval: 7, startDelay: 0 },
        { enemyType: 'combo199', count: 4, interval: 5, startDelay: 5 },
      ],
    },
    // 17 — two Chippingtons over a Corroder wall.
    {
      groups: [
        { enemyType: 'seasonezchippington', count: 2, interval: 10, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 8, interval: 2, startDelay: 4 },
        { enemyType: 'microwavednugget', count: 4, interval: 4, startDelay: 14 },
      ],
    },
    // 18 — Nachos, hot Nuggets, Nuggets.
    {
      groups: [
        { enemyType: 'nacho', count: 3, interval: 7, startDelay: 0 },
        { enemyType: 'microwavednugget', count: 6, interval: 3, startDelay: 4 },
        { enemyType: 'nuisancenugget', count: 4, interval: 5, startDelay: 16 },
      ],
    },
    // 19 — Chippingtons and Nachos.
    {
      groups: [
        { enemyType: 'seasonezchippington', count: 2, interval: 10, startDelay: 0 },
        { enemyType: 'nacho', count: 2, interval: 8, startDelay: 5 },
        { enemyType: 'combo199', count: 4, interval: 5, startDelay: 12 },
      ],
    },
    // 20 — the valley empties: everything, back to back.
    {
      groups: [
        { enemyType: 'nacho', count: 4, interval: 6, startDelay: 0 },
        { enemyType: 'seasonezchippington', count: 3, interval: 8, startDelay: 6 },
        { enemyType: 'microwavednugget', count: 6, interval: 3, startDelay: 14 },
        { enemyType: 'cosmiccorroder', count: 8, interval: 2, startDelay: 24 },
      ],
    },
  ],
};

const BUN_BEACH: LevelDef = {
  id: 107,
  chapter: 1,
  episode: 'VII',
  name: 'Bun Beach',
  subtitle: 'The Moon',
  background: 'bg-bunbeach',
  decor: 'beach',
  // A beach seen from above: bg-bunbeach keeps the sea (and its sailboats)
  // along the bottom band (y > 630), the beach bar in the top-right corner
  // and a few buns in the outer rim. The route runs along the shoreline: it
  // enters bottom-left just above the water, climbs into the sand and back
  // three times with a long shoreline leg in the middle, and leaves mid-right.
  path: [
    { x: 0, y: 560 },
    { x: 150, y: 560 },
    { x: 150, y: 190 },
    { x: 330, y: 190 },
    { x: 330, y: 560 },
    { x: 620, y: 560 },
    { x: 620, y: 190 },
    { x: 800, y: 190 },
    { x: 800, y: 560 },
    { x: 990, y: 560 },
    { x: 990, y: 380 },
    { x: 1040, y: 380 },
  ],
  // 15 plots: three down each corridor, two columns in the wide one over the
  // shoreline leg. Every pair is >= 100px apart and none comes within 60px of
  // the trail.
  plots: [
    { id: 0, pos: { x: 240, y: 300 } },
    { id: 1, pos: { x: 240, y: 400 } },
    { id: 2, pos: { x: 240, y: 500 } },
    { id: 3, pos: { x: 420, y: 300 } },
    { id: 4, pos: { x: 420, y: 400 } },
    { id: 5, pos: { x: 420, y: 500 } },
    { id: 6, pos: { x: 530, y: 300 } },
    { id: 7, pos: { x: 530, y: 400 } },
    { id: 8, pos: { x: 530, y: 500 } },
    { id: 9, pos: { x: 710, y: 300 } },
    { id: 10, pos: { x: 710, y: 400 } },
    { id: 11, pos: { x: 710, y: 500 } },
    { id: 12, pos: { x: 895, y: 300 } },
    { id: 13, pos: { x: 895, y: 400 } },
    { id: 14, pos: { x: 895, y: 500 } },
  ],
  startCash: 2800,
  startLives: 5,
  // The user's figures (spec 2026-09-09 §5).
  coinReward: 225,
  expReward: 400,
  towerHp: HUB_TOWER_HP,
  // Wise Hambi's warning, shown as the level opens.
  notice: {
    art: 'tower-wise-1',
    sender: 'Wise Hambi',
    text: 'Click those whistles when the lifeguard shows up, he is very annoying!',
  },
  // Nacho from wave 5, Chippington from wave 6, the Microwave on 8 and 16,
  // and THE LIFEGUARD on waves 10 and 18 only — he goes first, his whistles
  // start 3s after he arrives (see balance.ts `lifeguard`).
  waves: [
    // 1 — Corroders on the sand.
    {
      groups: [{ enemyType: 'cosmiccorroder', count: 4, interval: 2.5, startDelay: 0 }],
    },
    // 2 — Nuggets and Corroders.
    {
      groups: [
        { enemyType: 'nuisancenugget', count: 2, interval: 5, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 4, interval: 2.5, startDelay: 3 },
      ],
    },
    // 3 — Combos and hot Nuggets.
    {
      groups: [
        { enemyType: 'combo199', count: 2, interval: 6, startDelay: 0 },
        { enemyType: 'microwavednugget', count: 2, interval: 4, startDelay: 4 },
      ],
    },
    // 4 — hot Nuggets.
    {
      groups: [
        { enemyType: 'microwavednugget', count: 4, interval: 4, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 4, interval: 2.5, startDelay: 5 },
      ],
    },
    // 5 — NACHO, down from the valley.
    {
      groups: [
        { enemyType: 'nacho', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 5, interval: 2.5, startDelay: 5 },
        { enemyType: 'nuisancenugget', count: 2, interval: 5, startDelay: 10 },
      ],
    },
    // 6 — SEASONEZ CHIPPINGTON and Combos.
    {
      groups: [
        { enemyType: 'seasonezchippington', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'combo199', count: 3, interval: 5, startDelay: 5 },
      ],
    },
    // 7 — hot Nuggets and a Nacho.
    {
      groups: [
        { enemyType: 'microwavednugget', count: 5, interval: 3, startDelay: 0 },
        { enemyType: 'nacho', count: 1, interval: 1, startDelay: 8 },
        { enemyType: 'cosmiccorroder', count: 4, interval: 2.5, startDelay: 12 },
      ],
    },
    // 8 — THE MICROWAVE.
    {
      groups: [
        { enemyType: 'microwave', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'microwavednugget', count: 4, interval: 4, startDelay: 3 },
        { enemyType: 'combo199', count: 2, interval: 6, startDelay: 12 },
      ],
    },
    // 9 — Nachos and Chippington.
    {
      groups: [
        { enemyType: 'nacho', count: 2, interval: 8, startDelay: 0 },
        { enemyType: 'seasonezchippington', count: 1, interval: 1, startDelay: 6 },
        { enemyType: 'nuisancenugget', count: 3, interval: 5, startDelay: 10 },
      ],
    },
    // 10 — THE LIFEGUARD blows his whistle for the first time; his escort follows.
    {
      groups: [
        { enemyType: 'lifeguard', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 6, interval: 2, startDelay: 6 },
        { enemyType: 'microwavednugget', count: 3, interval: 4, startDelay: 14 },
      ],
    },
    // 11 — Combos and Nachos.
    {
      groups: [
        { enemyType: 'combo199', count: 4, interval: 5, startDelay: 0 },
        { enemyType: 'nacho', count: 2, interval: 8, startDelay: 5 },
      ],
    },
    // 12 — two Chippingtons.
    {
      groups: [
        { enemyType: 'seasonezchippington', count: 2, interval: 10, startDelay: 0 },
        { enemyType: 'microwavednugget', count: 5, interval: 3, startDelay: 5 },
      ],
    },
    // 13 — three Nachos over a Corroder wall.
    {
      groups: [
        { enemyType: 'nacho', count: 3, interval: 7, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 8, interval: 2, startDelay: 4 },
      ],
    },
    // 14 — Combos, Chippington, hot Nuggets.
    {
      groups: [
        { enemyType: 'combo199', count: 4, interval: 5, startDelay: 0 },
        { enemyType: 'seasonezchippington', count: 1, interval: 1, startDelay: 6 },
        { enemyType: 'microwavednugget', count: 4, interval: 4, startDelay: 12 },
      ],
    },
    // 15 — Nachos, Nuggets, hot Nuggets.
    {
      groups: [
        { enemyType: 'nacho', count: 2, interval: 8, startDelay: 0 },
        { enemyType: 'nuisancenugget', count: 5, interval: 5, startDelay: 3 },
        { enemyType: 'microwavednugget', count: 4, interval: 4, startDelay: 14 },
      ],
    },
    // 16 — the Microwave's second visit.
    {
      groups: [
        { enemyType: 'microwave', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'microwavednugget', count: 6, interval: 3, startDelay: 3 },
        { enemyType: 'seasonezchippington', count: 2, interval: 10, startDelay: 10 },
      ],
    },
    // 17 — Nachos, Combos, Corroders.
    {
      groups: [
        { enemyType: 'nacho', count: 3, interval: 7, startDelay: 0 },
        { enemyType: 'combo199', count: 4, interval: 5, startDelay: 5 },
        { enemyType: 'cosmiccorroder', count: 6, interval: 2, startDelay: 16 },
      ],
    },
    // 18 — THE LIFEGUARD again, with Nachos and Chippingtons.
    {
      groups: [
        { enemyType: 'lifeguard', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'nacho', count: 2, interval: 8, startDelay: 6 },
        { enemyType: 'seasonezchippington', count: 2, interval: 10, startDelay: 10 },
        { enemyType: 'microwavednugget', count: 4, interval: 4, startDelay: 18 },
      ],
    },
    // 19 — three Chippingtons.
    {
      groups: [
        { enemyType: 'seasonezchippington', count: 3, interval: 8, startDelay: 0 },
        { enemyType: 'microwavednugget', count: 6, interval: 3, startDelay: 5 },
        { enemyType: 'nuisancenugget', count: 4, interval: 5, startDelay: 16 },
      ],
    },
    // 20 — Nacho rush.
    {
      groups: [
        { enemyType: 'nacho', count: 4, interval: 6, startDelay: 0 },
        { enemyType: 'combo199', count: 5, interval: 5, startDelay: 5 },
        { enemyType: 'cosmiccorroder', count: 8, interval: 2, startDelay: 18 },
      ],
    },
    // 21 — Chippingtons and Nachos.
    {
      groups: [
        { enemyType: 'seasonezchippington', count: 3, interval: 8, startDelay: 0 },
        { enemyType: 'nacho', count: 3, interval: 7, startDelay: 5 },
        { enemyType: 'microwavednugget', count: 6, interval: 3, startDelay: 14 },
      ],
    },
    // 22 — last swim: everything the beach has, back to back.
    {
      groups: [
        { enemyType: 'nacho', count: 5, interval: 6, startDelay: 0 },
        { enemyType: 'seasonezchippington', count: 4, interval: 8, startDelay: 6 },
        { enemyType: 'microwavednugget', count: 8, interval: 3, startDelay: 14 },
        { enemyType: 'combo199', count: 4, interval: 5, startDelay: 26 },
        { enemyType: 'cosmiccorroder', count: 8, interval: 2, startDelay: 34 },
      ],
    },
  ],
};

const NACHYO_BUSINESS: LevelDef = {
  id: 108,
  chapter: 1,
  episode: 'VIII',
  name: "Nach'yo Business!",
  subtitle: 'The Moon',
  background: 'bg-nachyo',
  decor: 'nachyo',
  // A crater floor splattered with cheese, and a NACHO ONLY sign the game
  // letters itself (see GameScene.drawNachyoDecor). TWO nacho bases — one
  // top-left, one bottom-left — run inwards and meet at (180, 360), from
  // where a single trunk switchbacks across the crater and leaves mid-right.
  // Both lead-ins are 540px long, so an enemy's `distance` reads the same
  // whichever base it came out of (pinned by hubLevels.test.ts).
  entrances: [
    [
      { x: 0, y: 120 },
      { x: 180, y: 120 },
      { x: 180, y: 360 },
      { x: 300, y: 360 },
    ],
    [
      { x: 0, y: 600 },
      { x: 180, y: 600 },
      { x: 180, y: 360 },
      { x: 300, y: 360 },
    ],
  ],
  path: [
    { x: 300, y: 360 },
    { x: 480, y: 360 },
    { x: 480, y: 140 },
    { x: 700, y: 140 },
    { x: 700, y: 580 },
    { x: 920, y: 580 },
    { x: 920, y: 300 },
    { x: 1040, y: 300 },
  ],
  // 13 plots. The brief asks for plots BESIDE BOTH ENTRANCES, so the first
  // four cover the two lead-ins (0–1 the top base, 2–3 the bottom); the rest
  // line the trunk. None comes within 60px of any lane.
  plots: [
    { id: 0, pos: { x: 90, y: 240 } },
    { id: 1, pos: { x: 280, y: 200 } },
    { id: 2, pos: { x: 90, y: 480 } },
    { id: 3, pos: { x: 280, y: 500 } },
    { id: 4, pos: { x: 390, y: 250 } },
    { id: 5, pos: { x: 390, y: 480 } },
    { id: 6, pos: { x: 600, y: 260 } },
    { id: 7, pos: { x: 600, y: 430 } },
    { id: 8, pos: { x: 810, y: 260 } },
    { id: 9, pos: { x: 810, y: 430 } },
    { id: 10, pos: { x: 1010, y: 170 } },
    { id: 11, pos: { x: 1010, y: 440 } },
    { id: 12, pos: { x: 520, y: 620 } },
  ],
  startCash: 3000,
  startLives: 5,
  coinReward: 250,
  expReward: 450,
  towerHp: HUB_TOWER_HP,
  notice: {
    art: 'tower-wise-1',
    sender: 'Wise Hambi',
    text: 'Two nacho bases, one track. Cover both mouths or they walk right past you!',
  },
  // No Nuisance Nuggets, no Cosmic Corroders and no 1.99 Combos on this stage
  // or the next — the crater is nachos, the hot Nuggets and their Microwave.
  waves: [
    // 1 — the first two Moon Nachos, knives out.
    { groups: [{ enemyType: 'moonnacho', count: 2, interval: 4, startDelay: 0 }] },
    // 2 — one down each lane, and again.
    { groups: [{ enemyType: 'moonnacho', count: 4, interval: 3.5, startDelay: 0 }] },
    // 3 — plain Nachos walk the lanes with them.
    {
      groups: [
        { enemyType: 'moonnacho', count: 2, interval: 4, startDelay: 0 },
        { enemyType: 'nacho', count: 2, interval: 4, startDelay: 3 },
      ],
    },
    // 4 — the first SPICED NACHO: your towers start locking up.
    {
      groups: [
        { enemyType: 'spicednacho', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'moonnacho', count: 2, interval: 4, startDelay: 4 },
      ],
    },
    // 5 — a Nacho crowd.
    {
      groups: [
        { enemyType: 'nacho', count: 4, interval: 3, startDelay: 0 },
        { enemyType: 'moonnacho', count: 2, interval: 4, startDelay: 4 },
      ],
    },
    // 6 — two Spiced, so two towers go quiet at a time.
    {
      groups: [
        { enemyType: 'spicednacho', count: 2, interval: 6, startDelay: 0 },
        { enemyType: 'moonnacho', count: 3, interval: 3.5, startDelay: 3 },
      ],
    },
    // 7 — the hot Nuggets arrive.
    {
      groups: [
        { enemyType: 'microwavednugget', count: 4, interval: 3, startDelay: 0 },
        { enemyType: 'moonnacho', count: 2, interval: 4, startDelay: 5 },
      ],
    },
    // 8 — THE MICROWAVE, and everything it touches gets worse.
    {
      groups: [
        { enemyType: 'microwave', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'microwavednugget', count: 4, interval: 3, startDelay: 4 },
        { enemyType: 'moonnacho', count: 3, interval: 3.5, startDelay: 8 },
      ],
    },
    // 9 — Chippington's onion rings over the Spiced Nachos' locks.
    {
      groups: [
        { enemyType: 'seasonezchippington', count: 2, interval: 7, startDelay: 0 },
        { enemyType: 'spicednacho', count: 2, interval: 6, startDelay: 4 },
      ],
    },
    // 10 — a knife wall.
    {
      groups: [
        { enemyType: 'moonnacho', count: 6, interval: 2.5, startDelay: 0 },
        { enemyType: 'nacho', count: 4, interval: 3, startDelay: 6 },
      ],
    },
    // 11 — three Spiced: half your board can be down at once.
    {
      groups: [
        { enemyType: 'spicednacho', count: 3, interval: 5, startDelay: 0 },
        { enemyType: 'seasonezchippington', count: 2, interval: 7, startDelay: 6 },
      ],
    },
    // 12 — hot Nuggets and knives.
    {
      groups: [
        { enemyType: 'microwavednugget', count: 6, interval: 2.5, startDelay: 0 },
        { enemyType: 'moonnacho', count: 4, interval: 3, startDelay: 6 },
      ],
    },
    // 13 — the Microwave over three Spiced Nachos: 4-second locks.
    {
      groups: [
        { enemyType: 'microwave', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'spicednacho', count: 3, interval: 5, startDelay: 4 },
        { enemyType: 'moonnacho', count: 4, interval: 3, startDelay: 9 },
      ],
    },
    // 14 — a wall of chips.
    {
      groups: [
        { enemyType: 'nacho', count: 6, interval: 2.5, startDelay: 0 },
        { enemyType: 'seasonezchippington', count: 3, interval: 6, startDelay: 5 },
      ],
    },
    // 15 — knives and locks together.
    {
      groups: [
        { enemyType: 'moonnacho', count: 6, interval: 2.5, startDelay: 0 },
        { enemyType: 'spicednacho', count: 3, interval: 5, startDelay: 5 },
      ],
    },
    // 16 — the Microwave with its own Nuggets and two Spiced.
    {
      groups: [
        { enemyType: 'microwave', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'microwavednugget', count: 6, interval: 2.5, startDelay: 4 },
        { enemyType: 'spicednacho', count: 2, interval: 6, startDelay: 10 },
      ],
    },
    // 17 — Chippington escorted by knives.
    {
      groups: [
        { enemyType: 'seasonezchippington', count: 4, interval: 5, startDelay: 0 },
        { enemyType: 'moonnacho', count: 5, interval: 2.5, startDelay: 4 },
      ],
    },
    // 18 — four Spiced behind a Nacho screen.
    {
      groups: [
        { enemyType: 'spicednacho', count: 4, interval: 4.5, startDelay: 0 },
        { enemyType: 'nacho', count: 6, interval: 2.5, startDelay: 5 },
      ],
    },
    // 19 — the Microwave's last outing before the finish.
    {
      groups: [
        { enemyType: 'microwave', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'moonnacho', count: 6, interval: 2.5, startDelay: 4 },
        { enemyType: 'spicednacho', count: 3, interval: 5, startDelay: 10 },
      ],
    },
    // 20 — the whole crater, both mouths at once.
    {
      groups: [
        { enemyType: 'microwave', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'spicednacho', count: 4, interval: 4, startDelay: 3 },
        { enemyType: 'moonnacho', count: 8, interval: 2, startDelay: 8 },
        { enemyType: 'seasonezchippington', count: 4, interval: 5, startDelay: 16 },
        { enemyType: 'nacho', count: 8, interval: 2, startDelay: 24 },
      ],
    },
  ],
};

const ROCKET_RAGE: LevelDef = {
  id: 109,
  chapter: 1,
  episode: 'IX',
  name: 'Rocket Rage',
  subtitle: 'The Moon',
  background: 'bg-rocketrage',
  decor: 'rocketpad',
  // A launch pad with a GIANT ROCKET standing in the middle of it — a decor
  // sprite, not part of the background, because on wave 10 it has to leave
  // (see `transition` and GameScene.playRocketCutscene). The trail is routed
  // clear of the rocket's footprint (x 545–735, y 255–545): it runs the left
  // margin, crosses ABOVE the nose, comes down the right margin and exits
  // bottom-right.
  path: [
    { x: 0, y: 130 },
    { x: 150, y: 130 },
    { x: 150, y: 620 },
    { x: 400, y: 620 },
    { x: 400, y: 200 },
    { x: 880, y: 200 },
    { x: 880, y: 620 },
    { x: 1040, y: 620 },
  ],
  // 15 plots in the pockets the rocket and the trail leave: two outside the
  // first leg, three down the left corridor, four flanking the rocket, three
  // along the top, two on the right and one under the rocket's tail.
  plots: [
    { id: 0, pos: { x: 70, y: 350 } },
    { id: 1, pos: { x: 70, y: 500 } },
    { id: 2, pos: { x: 275, y: 300 } },
    { id: 3, pos: { x: 275, y: 430 } },
    { id: 4, pos: { x: 275, y: 550 } },
    { id: 5, pos: { x: 480, y: 350 } },
    { id: 6, pos: { x: 480, y: 500 } },
    { id: 7, pos: { x: 800, y: 350 } },
    { id: 8, pos: { x: 800, y: 500 } },
    { id: 9, pos: { x: 640, y: 625 } },
    { id: 10, pos: { x: 500, y: 120 } },
    { id: 11, pos: { x: 700, y: 120 } },
    { id: 12, pos: { x: 900, y: 120 } },
    { id: 13, pos: { x: 990, y: 350 } },
    { id: 14, pos: { x: 990, y: 480 } },
  ],
  startCash: 3400,
  startLives: 5,
  coinReward: 300,
  expReward: 550,
  towerHp: HUB_TOWER_HP,
  // Wave 10 is where the rocket goes. The board does not move an inch — the
  // ground under it does. A one-element list: `transitions` is a list because
  // the chapter finale has two, and nothing here changes on screen.
  transitions: [
    {
      wave: 10,
      background: 'bg-gasgiant',
      trackStyle: 'rocks',
      plotBase: 'decor-floatingrock',
      decor: 'floatingrocks',
    },
  ],
  notice: {
    art: 'tower-wise-1',
    sender: 'Wise Hambi',
    text: 'Do not stand under the rocket. On wave 10 it is leaving, and so are we.',
  },
  // Laser Nachos only appear AFTER the launch — the minigun crowd is waiting
  // out at the gas giant, not on the pad.
  waves: [
    // 1 — knives on the pad.
    { groups: [{ enemyType: 'moonnacho', count: 3, interval: 3.5, startDelay: 0 }] },
    // 2 — more of them.
    { groups: [{ enemyType: 'moonnacho', count: 4, interval: 3, startDelay: 0 }] },
    // 3 — plain Nachos join.
    {
      groups: [
        { enemyType: 'nacho', count: 4, interval: 3, startDelay: 0 },
        { enemyType: 'moonnacho', count: 2, interval: 4, startDelay: 4 },
      ],
    },
    // 4 — the Spiced Nachos start locking towers.
    {
      groups: [
        { enemyType: 'spicednacho', count: 2, interval: 6, startDelay: 0 },
        { enemyType: 'moonnacho', count: 3, interval: 3.5, startDelay: 4 },
      ],
    },
    // 5 — hot Nuggets.
    {
      groups: [
        { enemyType: 'microwavednugget', count: 4, interval: 3, startDelay: 0 },
        { enemyType: 'moonnacho', count: 3, interval: 3.5, startDelay: 5 },
      ],
    },
    // 6 — THE MICROWAVE on the pad.
    {
      groups: [
        { enemyType: 'microwave', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'microwavednugget', count: 4, interval: 3, startDelay: 4 },
        { enemyType: 'moonnacho', count: 3, interval: 3.5, startDelay: 9 },
      ],
    },
    // 7 — Chippington and the Spiced.
    {
      groups: [
        { enemyType: 'seasonezchippington', count: 2, interval: 7, startDelay: 0 },
        { enemyType: 'spicednacho', count: 2, interval: 6, startDelay: 4 },
      ],
    },
    // 8 — a chip crowd.
    {
      groups: [
        { enemyType: 'nacho', count: 6, interval: 2.5, startDelay: 0 },
        { enemyType: 'moonnacho', count: 4, interval: 3, startDelay: 6 },
      ],
    },
    // 9 — the last wave on the moon.
    {
      groups: [
        { enemyType: 'spicednacho', count: 3, interval: 5, startDelay: 0 },
        { enemyType: 'seasonezchippington', count: 2, interval: 7, startDelay: 5 },
        { enemyType: 'moonnacho', count: 3, interval: 3.5, startDelay: 12 },
      ],
    },
    // ── 10: the rocket leaves, and the fight carries on at the gas giant ──
    // 10 — the first LASER NACHOS, waiting out here for you. NOTHING spawns at
    // startDelay 0: the cutscene is armed one frame after the wave begins, and
    // the sim has already stepped that frame, so a 0-delay enemy would be
    // standing on the field when the cover lifts.
    {
      groups: [
        { enemyType: 'lasernacho', count: 2, interval: 6, startDelay: 1.5 },
        { enemyType: 'moonnacho', count: 3, interval: 3.5, startDelay: 5 },
      ],
    },
    // 11 — three beams and two locks.
    {
      groups: [
        { enemyType: 'lasernacho', count: 3, interval: 5, startDelay: 0 },
        { enemyType: 'spicednacho', count: 2, interval: 6, startDelay: 5 },
      ],
    },
    // 12 — a Nacho screen for the beams behind it.
    {
      groups: [
        { enemyType: 'nacho', count: 6, interval: 2.5, startDelay: 0 },
        { enemyType: 'lasernacho', count: 2, interval: 6, startDelay: 5 },
      ],
    },
    // 13 — the Microwave halves the Lasers' cooldown.
    {
      groups: [
        { enemyType: 'microwave', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'lasernacho', count: 3, interval: 5, startDelay: 4 },
        { enemyType: 'moonnacho', count: 4, interval: 3, startDelay: 10 },
      ],
    },
    // 14 — onion rings and locks.
    {
      groups: [
        { enemyType: 'seasonezchippington', count: 3, interval: 6, startDelay: 0 },
        { enemyType: 'spicednacho', count: 3, interval: 5, startDelay: 5 },
      ],
    },
    // 15 — four beams.
    {
      groups: [
        { enemyType: 'lasernacho', count: 4, interval: 4.5, startDelay: 0 },
        { enemyType: 'nacho', count: 6, interval: 2.5, startDelay: 6 },
      ],
    },
    // 16 — the Microwave's Nuggets under covering fire.
    {
      groups: [
        { enemyType: 'microwave', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'microwavednugget', count: 6, interval: 2.5, startDelay: 4 },
        { enemyType: 'lasernacho', count: 3, interval: 5, startDelay: 10 },
      ],
    },
    // 17 — knives and locks, no beams for one wave.
    {
      groups: [
        { enemyType: 'spicednacho', count: 4, interval: 4.5, startDelay: 0 },
        { enemyType: 'moonnacho', count: 6, interval: 2.5, startDelay: 5 },
      ],
    },
    // 18 — beams behind Chippingtons.
    {
      groups: [
        { enemyType: 'lasernacho', count: 4, interval: 4.5, startDelay: 0 },
        { enemyType: 'seasonezchippington', count: 3, interval: 6, startDelay: 5 },
      ],
    },
    // 19 — a wall of chips.
    {
      groups: [
        { enemyType: 'nacho', count: 8, interval: 2, startDelay: 0 },
        { enemyType: 'spicednacho', count: 3, interval: 5, startDelay: 8 },
      ],
    },
    // 20 — the Microwave over four Lasers and three Spiced.
    {
      groups: [
        { enemyType: 'microwave', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'lasernacho', count: 4, interval: 4.5, startDelay: 4 },
        { enemyType: 'spicednacho', count: 3, interval: 5, startDelay: 12 },
      ],
    },
    // 21 — knives in bulk.
    {
      groups: [
        { enemyType: 'moonnacho', count: 8, interval: 2, startDelay: 0 },
        { enemyType: 'lasernacho', count: 3, interval: 5, startDelay: 8 },
      ],
    },
    // 22 — four Chippingtons, four Spiced.
    {
      groups: [
        { enemyType: 'seasonezchippington', count: 4, interval: 5, startDelay: 0 },
        { enemyType: 'spicednacho', count: 4, interval: 4.5, startDelay: 5 },
      ],
    },
    // 23 — TWO Microwaves: every nacho on the field is buffed.
    {
      groups: [
        { enemyType: 'microwave', count: 2, interval: 8, startDelay: 0 },
        { enemyType: 'lasernacho', count: 4, interval: 4.5, startDelay: 5 },
        { enemyType: 'moonnacho', count: 6, interval: 2.5, startDelay: 13 },
      ],
    },
    // 24 — everything, at pace.
    {
      groups: [
        { enemyType: 'lasernacho', count: 4, interval: 4.5, startDelay: 0 },
        { enemyType: 'spicednacho', count: 4, interval: 4.5, startDelay: 5 },
        { enemyType: 'moonnacho', count: 6, interval: 2.5, startDelay: 12 },
        { enemyType: 'seasonezchippington', count: 3, interval: 6, startDelay: 20 },
      ],
    },
    // 25 — the last wave: two Microwaves and the whole nacho roster.
    {
      groups: [
        { enemyType: 'microwave', count: 2, interval: 10, startDelay: 0 },
        { enemyType: 'lasernacho', count: 5, interval: 4, startDelay: 4 },
        { enemyType: 'spicednacho', count: 5, interval: 4, startDelay: 12 },
        { enemyType: 'moonnacho', count: 8, interval: 2, startDelay: 20 },
        { enemyType: 'seasonezchippington', count: 4, interval: 5, startDelay: 30 },
        { enemyType: 'nacho', count: 8, interval: 2, startDelay: 38 },
      ],
    },
  ],
};

const THROUGH_SPACE_AND_TIME: LevelDef = {
  id: 110,
  chapter: 1,
  episode: 'X',
  name: 'Through Space and Time',
  subtitle: 'The Moon',
  background: 'bg-spacetime',
  decor: 'spaceplatform',
  // THE MOON'S FINALE. A huge grey platform hanging in space with the rocket
  // standing on it, and the whole track laid out on the deck. THREE nacho
  // bases feed one trunk; the rocket leaves on wave 10 for the ringed planet
  // and again on wave 20 for the ice world, and neither trip moves the board.
  //
  // All three lead-ins are 590px long — deliberately equal, so an enemy's
  // `distance` reads the same whichever mouth it came out of — and all three
  // converge on (340, 360): one from above, one from the left, one from below.
  entrances: [
    [
      { x: 0, y: 110 },
      { x: 160, y: 110 },
      { x: 160, y: 250 },
      { x: 340, y: 250 },
      { x: 340, y: 360 },
    ],
    [
      { x: 0, y: 360 },
      { x: 110, y: 360 },
      { x: 110, y: 485 },
      { x: 230, y: 485 },
      { x: 230, y: 360 },
      { x: 340, y: 360 },
    ],
    [
      { x: 0, y: 610 },
      { x: 340, y: 610 },
      { x: 340, y: 360 },
    ],
  ],
  // The trunk climbs the left, runs the top at y = 150, comes down the right
  // and doubles back at y = 430 before leaving bottom-right. The pocket it
  // leaves between those two legs — x 500..780, y 160..400 — is deliberately
  // EMPTY of trail and plots: the rocket stands in it until wave 10, and
  // Prince Nacho's second form lands in it on wave 30 (`transformsInto.at`).
  // `hubLevels.test.ts` pins that the pocket stays clear.
  path: [
    { x: 340, y: 360 },
    { x: 420, y: 360 },
    { x: 420, y: 150 },
    { x: 1000, y: 150 },
    { x: 1000, y: 430 },
    { x: 640, y: 430 },
    { x: 640, y: 620 },
    { x: 1040, y: 620 },
  ],
  // 16 plots, none closer than 112px to another and none within 60px of any
  // lane (lead-ins included) — and none inside the boss pocket. Plots 0–2, 9
  // and 10 cover the three mouths; the rest line the trunk.
  plots: [
    { id: 0, pos: { x: 290, y: 120 } },
    { id: 1, pos: { x: 60, y: 255 } },
    { id: 2, pos: { x: 170, y: 320 } },
    { id: 3, pos: { x: 495, y: 300 } },
    { id: 4, pos: { x: 860, y: 290 } },
    { id: 5, pos: { x: 940, y: 210 } },
    { id: 6, pos: { x: 415, y: 435 } },
    { id: 7, pos: { x: 525, y: 410 } },
    { id: 8, pos: { x: 940, y: 370 } },
    { id: 9, pos: { x: 60, y: 535 } },
    { id: 10, pos: { x: 270, y: 540 } },
    { id: 11, pos: { x: 490, y: 520 } },
    { id: 12, pos: { x: 780, y: 525 } },
    { id: 13, pos: { x: 895, y: 525 } },
    { id: 14, pos: { x: 1010, y: 525 } },
    { id: 15, pos: { x: 490, y: 635 } },
  ],
  startCash: 4000,
  startLives: 5,
  coinReward: 500,
  expReward: 1000,
  towerHp: HUB_TOWER_HP,
  // Two changes of scene, in wave order. Neither moves the board an inch —
  // same route, same plots, same towers standing on them. Only the ground and
  // the sky are swapped, which is what sells the idea that they made the trip.
  transitions: [
    {
      wave: 10,
      background: 'bg-ringedplanet',
      trackStyle: 'rocks',
      plotBase: 'decor-floatingrock',
      decor: 'floatingrocks',
      caption: 'The rocket is leaving...',
      captionEnd: '...and it is a long way down.',
    },
    {
      wave: 20,
      background: 'bg-iceplanet',
      trackStyle: 'rocks',
      plotBase: 'decor-iceblock',
      decor: 'icesculptures',
      caption: 'The rocket is leaving AGAIN...',
      captionEnd: '...and something is waiting out here.',
    },
  ],
  notice: {
    art: 'tower-wise-1',
    sender: 'Wise Hambi',
    text: 'Three bases, thirty waves and two launches. Whatever is at the end of this, it is wearing a crown.',
  },
  // The whole Moon roster walks this level — every ordinary enemy of the
  // chapter, the Bar Waiter included. The chapter's EARLIER bosses (Chiply,
  // Chiply's Dad, The Lifeguard) stay out on purpose: their press-the-target
  // prompts would collide with Prince Nacho's shields (spec §1).
  waves: [
    // ── The platform (1–9) ─────────────────────────────────────────────────
    // 1 — Corroders test the deck, one down each of the three mouths.
    { groups: [{ enemyType: 'cosmiccorroder', count: 3, interval: 3.5, startDelay: 0 }] },
    // 2 — and a Nugget with them.
    {
      groups: [
        { enemyType: 'cosmiccorroder', count: 4, interval: 3, startDelay: 0 },
        { enemyType: 'nuisancenugget', count: 1, interval: 1, startDelay: 4 },
      ],
    },
    // 3 — the knives arrive.
    { groups: [{ enemyType: 'moonnacho', count: 3, interval: 3.5, startDelay: 0 }] },
    // 4 — the Bar Waiter is on the platform too; everyone came.
    {
      groups: [
        { enemyType: 'barwaiter', count: 2, interval: 6, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 3, interval: 3, startDelay: 3 },
      ],
    },
    // 5 — a chip crowd.
    {
      groups: [
        { enemyType: 'nacho', count: 5, interval: 2.5, startDelay: 0 },
        { enemyType: 'moonnacho', count: 2, interval: 4, startDelay: 5 },
      ],
    },
    // 6 — hot Nuggets.
    {
      groups: [
        { enemyType: 'microwavednugget', count: 4, interval: 3, startDelay: 0 },
        { enemyType: 'nuisancenugget', count: 2, interval: 5, startDelay: 5 },
      ],
    },
    // 7 — the first locks.
    {
      groups: [
        { enemyType: 'spicednacho', count: 2, interval: 6, startDelay: 0 },
        { enemyType: 'moonnacho', count: 3, interval: 3.5, startDelay: 4 },
      ],
    },
    // 8 — Burnt Fries out of the exhibition.
    {
      groups: [
        { enemyType: 'burntfry', count: 3, interval: 3, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 4, interval: 3, startDelay: 5 },
      ],
    },
    // 9 — the last wave on the platform.
    {
      groups: [
        { enemyType: 'combo199', count: 3, interval: 5, startDelay: 0 },
        { enemyType: 'nacho', count: 5, interval: 2.5, startDelay: 6 },
      ],
    },
    // ── The ringed planet (10–19) ──────────────────────────────────────────
    // 10 — NOTHING at startDelay 0: the cutscene arms one frame after the wave
    // begins, and a 0-delay enemy would be standing on the field when the
    // cover lifts (the rule Rocket Rage's wave 10 set).
    {
      groups: [
        { enemyType: 'lasernacho', count: 2, interval: 6, startDelay: 1.5 },
        { enemyType: 'moonnacho', count: 3, interval: 3.5, startDelay: 5 },
      ],
    },
    // 11 — THE MICROWAVE out at the rings.
    {
      groups: [
        { enemyType: 'microwave', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'microwavednugget', count: 5, interval: 3, startDelay: 4 },
        { enemyType: 'moonnacho', count: 3, interval: 3.5, startDelay: 10 },
      ],
    },
    // 12 — onion rings over the locks.
    {
      groups: [
        { enemyType: 'seasonezchippington', count: 3, interval: 6, startDelay: 0 },
        { enemyType: 'spicednacho', count: 2, interval: 6, startDelay: 4 },
      ],
    },
    // 13 — beams behind a chip screen.
    {
      groups: [
        { enemyType: 'lasernacho', count: 3, interval: 5, startDelay: 0 },
        { enemyType: 'nacho', count: 6, interval: 2.5, startDelay: 5 },
      ],
    },
    // 14 — the old guard: Nuggets, Corroders and Fries.
    {
      groups: [
        { enemyType: 'nuisancenugget', count: 3, interval: 4, startDelay: 0 },
        { enemyType: 'cosmiccorroder', count: 5, interval: 2.5, startDelay: 4 },
        { enemyType: 'burntfry', count: 3, interval: 3, startDelay: 10 },
      ],
    },
    // 15 — the Microwave halves the Lasers' cooldown and lengthens the locks.
    {
      groups: [
        { enemyType: 'microwave', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'spicednacho', count: 3, interval: 5, startDelay: 4 },
        { enemyType: 'lasernacho', count: 3, interval: 5, startDelay: 11 },
      ],
    },
    // 16 — Combos and Chippingtons.
    {
      groups: [
        { enemyType: 'combo199', count: 4, interval: 4.5, startDelay: 0 },
        { enemyType: 'seasonezchippington', count: 3, interval: 6, startDelay: 5 },
      ],
    },
    // 17 — a wall of chips.
    {
      groups: [
        { enemyType: 'moonnacho', count: 7, interval: 2.5, startDelay: 0 },
        { enemyType: 'nacho', count: 6, interval: 2.5, startDelay: 8 },
      ],
    },
    // 18 — four beams and three locks.
    {
      groups: [
        { enemyType: 'lasernacho', count: 4, interval: 4.5, startDelay: 0 },
        { enemyType: 'spicednacho', count: 3, interval: 5, startDelay: 6 },
      ],
    },
    // 19 — the last wave at the rings.
    {
      groups: [
        { enemyType: 'microwave', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'microwavednugget', count: 6, interval: 2.5, startDelay: 4 },
        { enemyType: 'seasonezchippington', count: 3, interval: 6, startDelay: 12 },
      ],
    },
    // ── The ice world (20–30) ──────────────────────────────────────────────
    // 20 — the second launch; nothing at startDelay 0, same rule as wave 10.
    {
      groups: [
        { enemyType: 'lasernacho', count: 3, interval: 5, startDelay: 1.5 },
        { enemyType: 'moonnacho', count: 5, interval: 2.5, startDelay: 7 },
        { enemyType: 'cosmiccorroder', count: 4, interval: 3, startDelay: 16 },
      ],
    },
    // 21 — four locks behind four Chippingtons.
    {
      groups: [
        { enemyType: 'spicednacho', count: 4, interval: 4.5, startDelay: 0 },
        { enemyType: 'seasonezchippington', count: 4, interval: 5, startDelay: 5 },
      ],
    },
    // 22 — TWO Microwaves: every nacho on the ice is buffed.
    {
      groups: [
        { enemyType: 'microwave', count: 2, interval: 8, startDelay: 0 },
        { enemyType: 'lasernacho', count: 4, interval: 4.5, startDelay: 5 },
        { enemyType: 'moonnacho', count: 6, interval: 2.5, startDelay: 13 },
      ],
    },
    // 23 — Combos, Fries and a chip crowd.
    {
      groups: [
        { enemyType: 'combo199', count: 5, interval: 4, startDelay: 0 },
        { enemyType: 'burntfry', count: 4, interval: 2.5, startDelay: 6 },
        { enemyType: 'nacho', count: 8, interval: 2, startDelay: 14 },
      ],
    },
    // 24 — the bar staff, one last time.
    {
      groups: [
        { enemyType: 'nuisancenugget', count: 4, interval: 3.5, startDelay: 0 },
        { enemyType: 'barwaiter', count: 3, interval: 5, startDelay: 5 },
        { enemyType: 'cosmiccorroder', count: 6, interval: 2, startDelay: 13 },
      ],
    },
    // 25 — five locks under a Microwave: half the board can be down at once.
    {
      groups: [
        { enemyType: 'microwave', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'spicednacho', count: 5, interval: 4, startDelay: 4 },
        { enemyType: 'lasernacho', count: 4, interval: 4.5, startDelay: 14 },
      ],
    },
    // 26 — onion rings and knives.
    {
      groups: [
        { enemyType: 'seasonezchippington', count: 5, interval: 4.5, startDelay: 0 },
        { enemyType: 'moonnacho', count: 8, interval: 2, startDelay: 5 },
      ],
    },
    // 27 — two Microwaves over the hot Nuggets and the beams.
    {
      groups: [
        { enemyType: 'microwave', count: 2, interval: 9, startDelay: 0 },
        { enemyType: 'microwavednugget', count: 8, interval: 2, startDelay: 4 },
        { enemyType: 'lasernacho', count: 4, interval: 4.5, startDelay: 14 },
      ],
    },
    // 28 — a chip avalanche.
    {
      groups: [
        { enemyType: 'nacho', count: 10, interval: 1.8, startDelay: 0 },
        { enemyType: 'spicednacho', count: 4, interval: 4.5, startDelay: 8 },
        { enemyType: 'seasonezchippington', count: 4, interval: 5, startDelay: 18 },
      ],
    },
    // 29 — everything the chapter has, at once. Clear this and he comes.
    {
      groups: [
        { enemyType: 'microwave', count: 2, interval: 10, startDelay: 0 },
        { enemyType: 'lasernacho', count: 5, interval: 4, startDelay: 4 },
        { enemyType: 'spicednacho', count: 5, interval: 4, startDelay: 12 },
        { enemyType: 'moonnacho', count: 8, interval: 2, startDelay: 22 },
        { enemyType: 'seasonezchippington', count: 4, interval: 5, startDelay: 30 },
      ],
    },
    // 30 — PRINCE NACHO, first out, with a nacho honour guard behind him.
    // Killing him cancels whatever of the guard has not walked yet: his second
    // form clears the wave runners (see EnemySpec.transformsInto).
    {
      groups: [
        { enemyType: 'princenacho', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'moonnacho', count: 6, interval: 3, startDelay: 6 },
        { enemyType: 'nacho', count: 6, interval: 3, startDelay: 20 },
      ],
    },
  ],
};

// ── Chapter 02: The Fungi Forest (spec 2026-10-20-fungi-forest-design.md) ──
// Unlocks after The Moon (the Hub keeps ONE sequential cursor), fields ONLY
// the three shrooms, and pays on the Moon's ladder. No music yet.

/**
 * Village of Veg's mushroom-roofed houses: [x, y, size]. Placed by the game,
 * not baked into bg-villageofveg; hubLevels.test keeps every one 80px off the
 * trail and 90px off every plot.
 */
export const SHROOM_VILLAGE_HOUSES: readonly [number, number, number][] = [
  [455, 320, 110],
  [600, 320, 110],
  [455, 450, 110],
  [600, 450, 110],
];

const MUSHROOM_MANGROVE: LevelDef = {
  id: 111,
  chapter: 2,
  episode: 'I',
  name: 'Mushroom Mangrove',
  subtitle: 'The Fungi Forest',
  background: 'bg-mushroommangrove',
  // Giant red-and-white mushrooms ring the clearing and mangrove pools sit in
  // the corners, so the route enters mid-left through a gap in the left-hand
  // mushrooms and walks four switchback legs across the open grass (y 170 to
  // 540), clear of the caps along the top and bottom.
  path: [
    { x: 0, y: 350 },
    { x: 220, y: 350 },
    { x: 220, y: 170 },
    { x: 460, y: 170 },
    { x: 460, y: 540 },
    { x: 700, y: 540 },
    { x: 700, y: 170 },
    { x: 920, y: 170 },
    { x: 920, y: 420 },
    { x: 1040, y: 420 },
  ],
  plots: [
    { id: 0, pos: { x: 130, y: 250 } },
    { id: 1, pos: { x: 130, y: 450 } },
    { id: 2, pos: { x: 340, y: 250 } },
    { id: 3, pos: { x: 340, y: 370 } },
    { id: 4, pos: { x: 340, y: 490 } },
    { id: 5, pos: { x: 580, y: 250 } },
    { id: 6, pos: { x: 580, y: 360 } },
    { id: 7, pos: { x: 580, y: 470 } },
    { id: 8, pos: { x: 810, y: 260 } },
    { id: 9, pos: { x: 810, y: 370 } },
    { id: 10, pos: { x: 810, y: 480 } },
    { id: 11, pos: { x: 1000, y: 300 } },
    { id: 12, pos: { x: 1000, y: 530 } },
  ],
  startCash: 2000,
  startLives: 5,
  coinReward: 300,
  expReward: 550,
  towerHp: HUB_TOWER_HP,
  waves: [
    // 1 — a pair of Redshrooms hop in.
    {
      groups: [{ enemyType: 'redshroom', count: 2, interval: 4, startDelay: 0 }],
    },
    // 2 — three more.
    {
      groups: [{ enemyType: 'redshroom', count: 3, interval: 3.5, startDelay: 0 }],
    },
    // 3 — a line of four.
    {
      groups: [{ enemyType: 'redshroom', count: 4, interval: 3, startDelay: 0 }],
    },
    // 4 — the first Poison Shroom.
    {
      groups: [
        { enemyType: 'poisonshroom', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'redshroom', count: 2, interval: 4, startDelay: 3 },
      ],
    },
    // 5 — Redshrooms with a Poison Shroom behind them.
    {
      groups: [
        { enemyType: 'redshroom', count: 4, interval: 3, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 1, interval: 1, startDelay: 8 },
      ],
    },
    // 6 — two Poison Shrooms.
    {
      groups: [
        { enemyType: 'poisonshroom', count: 2, interval: 5, startDelay: 0 },
        { enemyType: 'redshroom', count: 3, interval: 3, startDelay: 4 },
      ],
    },
    // 7 — a fast red line.
    {
      groups: [{ enemyType: 'redshroom', count: 6, interval: 2.5, startDelay: 0 }],
    },
    // 8 — the poison round.
    {
      groups: [
        { enemyType: 'poisonshroom', count: 3, interval: 4, startDelay: 0 },
        { enemyType: 'redshroom', count: 2, interval: 3, startDelay: 6 },
      ],
    },
    // 9 — red, then green.
    {
      groups: [
        { enemyType: 'redshroom', count: 5, interval: 2.5, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 2, interval: 5, startDelay: 5 },
      ],
    },
    // 10 — four Poison Shrooms.
    {
      groups: [
        { enemyType: 'poisonshroom', count: 4, interval: 3.5, startDelay: 0 },
        { enemyType: 'redshroom', count: 3, interval: 3, startDelay: 8 },
      ],
    },
    // 11 — eight Redshrooms.
    {
      groups: [
        { enemyType: 'redshroom', count: 8, interval: 2, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 2, interval: 5, startDelay: 6 },
      ],
    },
    // 12 — poison under red cover.
    {
      groups: [
        { enemyType: 'poisonshroom', count: 5, interval: 3, startDelay: 0 },
        { enemyType: 'redshroom', count: 4, interval: 2.5, startDelay: 6 },
      ],
    },
    // 13 — two red packs around the poison.
    {
      groups: [
        { enemyType: 'redshroom', count: 6, interval: 2, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 3, interval: 4, startDelay: 4 },
        { enemyType: 'redshroom', count: 4, interval: 2, startDelay: 16 },
      ],
    },
    // 14 — six Poison Shrooms.
    {
      groups: [
        { enemyType: 'poisonshroom', count: 6, interval: 3, startDelay: 0 },
        { enemyType: 'redshroom', count: 6, interval: 2, startDelay: 5 },
      ],
    },
    // 15 — ten Redshrooms.
    {
      groups: [
        { enemyType: 'redshroom', count: 10, interval: 2, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 4, interval: 3, startDelay: 8 },
      ],
    },
    // 16 — the whole mangrove, back to back.
    {
      groups: [
        { enemyType: 'redshroom', count: 8, interval: 2, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 5, interval: 3, startDelay: 6 },
        { enemyType: 'redshroom', count: 8, interval: 2, startDelay: 20 },
        { enemyType: 'poisonshroom', count: 3, interval: 3, startDelay: 30 },
      ],
    },
  ],
};

const VILLAGE_OF_VEG: LevelDef = {
  id: 112,
  chapter: 2,
  episode: 'II',
  name: 'Village of Veg',
  subtitle: 'The Fungi Forest',
  background: 'bg-villageofveg',
  decor: 'shroomvillage',
  // A clearing ringed by trees and vegetable patches. The mushroom-roofed
  // houses in the middle are decor the GAME places (SHROOM_VILLAGE_HOUSES),
  // since the generator cannot see the track: the route loops round the
  // village (x 330..720, y 180..580) and leaves mid-right.
  path: [
    { x: 0, y: 180 },
    { x: 330, y: 180 },
    { x: 330, y: 580 },
    { x: 720, y: 580 },
    { x: 720, y: 180 },
    { x: 900, y: 180 },
    { x: 900, y: 480 },
    { x: 1040, y: 480 },
  ],
  plots: [
    { id: 0, pos: { x: 180, y: 280 } },
    { id: 1, pos: { x: 180, y: 400 } },
    { id: 2, pos: { x: 180, y: 520 } },
    { id: 3, pos: { x: 440, y: 110 } },
    { id: 4, pos: { x: 560, y: 110 } },
    { id: 5, pos: { x: 680, y: 110 } },
    { id: 6, pos: { x: 810, y: 290 } },
    { id: 7, pos: { x: 810, y: 410 } },
    { id: 8, pos: { x: 810, y: 540 } },
    { id: 9, pos: { x: 560, y: 640 } },
    { id: 10, pos: { x: 250, y: 620 } },
    { id: 11, pos: { x: 1000, y: 330 } },
    { id: 12, pos: { x: 1000, y: 600 } },
  ],
  startCash: 2200,
  startLives: 5,
  coinReward: 325,
  expReward: 600,
  towerHp: HUB_TOWER_HP,
  waves: [
    // 1 — Redshrooms on the village road.
    {
      groups: [{ enemyType: 'redshroom', count: 3, interval: 3.5, startDelay: 0 }],
    },
    // 2 — a Poison Shroom follows.
    {
      groups: [
        { enemyType: 'redshroom', count: 3, interval: 3, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 1, interval: 1, startDelay: 6 },
      ],
    },
    // 3 — the first Skullshroom.
    {
      groups: [
        { enemyType: 'skullshroom', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'redshroom', count: 2, interval: 4, startDelay: 4 },
      ],
    },
    // 4 — poison, then a skull.
    {
      groups: [
        { enemyType: 'poisonshroom', count: 2, interval: 4, startDelay: 0 },
        { enemyType: 'skullshroom', count: 1, interval: 1, startDelay: 6 },
      ],
    },
    // 5 — a red line and a skull.
    {
      groups: [
        { enemyType: 'redshroom', count: 5, interval: 2.5, startDelay: 0 },
        { enemyType: 'skullshroom', count: 1, interval: 1, startDelay: 8 },
      ],
    },
    // 6 — two Skullshrooms.
    {
      groups: [
        { enemyType: 'skullshroom', count: 2, interval: 5, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 2, interval: 4, startDelay: 4 },
      ],
    },
    // 7 — red and green.
    {
      groups: [
        { enemyType: 'redshroom', count: 6, interval: 2.5, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 2, interval: 4, startDelay: 6 },
      ],
    },
    // 8 — three skulls.
    {
      groups: [
        { enemyType: 'skullshroom', count: 3, interval: 4, startDelay: 0 },
        { enemyType: 'redshroom', count: 3, interval: 3, startDelay: 6 },
      ],
    },
    // 9 — the poison round.
    {
      groups: [
        { enemyType: 'poisonshroom', count: 4, interval: 3, startDelay: 0 },
        { enemyType: 'skullshroom', count: 2, interval: 5, startDelay: 5 },
      ],
    },
    // 10 — eight Redshrooms and two skulls.
    {
      groups: [
        { enemyType: 'redshroom', count: 8, interval: 2, startDelay: 0 },
        { enemyType: 'skullshroom', count: 2, interval: 5, startDelay: 6 },
      ],
    },
    // 11 — four Skullshrooms.
    {
      groups: [
        { enemyType: 'skullshroom', count: 4, interval: 3.5, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 3, interval: 4, startDelay: 6 },
      ],
    },
    // 12 — everything, a little of each.
    {
      groups: [
        { enemyType: 'redshroom', count: 6, interval: 2, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 3, interval: 3, startDelay: 4 },
        { enemyType: 'skullshroom', count: 2, interval: 5, startDelay: 12 },
      ],
    },
    // 13 — five skulls.
    {
      groups: [
        { enemyType: 'skullshroom', count: 5, interval: 3, startDelay: 0 },
        { enemyType: 'redshroom', count: 5, interval: 2, startDelay: 6 },
      ],
    },
    // 14 — six Poison Shrooms.
    {
      groups: [
        { enemyType: 'poisonshroom', count: 6, interval: 2.5, startDelay: 0 },
        { enemyType: 'skullshroom', count: 3, interval: 4, startDelay: 8 },
      ],
    },
    // 15 — ten Redshrooms.
    {
      groups: [
        { enemyType: 'redshroom', count: 10, interval: 2, startDelay: 0 },
        { enemyType: 'skullshroom', count: 4, interval: 3, startDelay: 8 },
      ],
    },
    // 16 — six skulls with poison behind.
    {
      groups: [
        { enemyType: 'skullshroom', count: 6, interval: 3, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 4, interval: 3, startDelay: 6 },
      ],
    },
    // 17 — the village is overrun.
    {
      groups: [
        { enemyType: 'redshroom', count: 8, interval: 2, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 4, interval: 3, startDelay: 6 },
        { enemyType: 'skullshroom', count: 4, interval: 3, startDelay: 14 },
      ],
    },
    // 18 — the last stand of the Skullshrooms.
    {
      groups: [
        { enemyType: 'skullshroom', count: 8, interval: 2.5, startDelay: 0 },
        { enemyType: 'redshroom', count: 8, interval: 2, startDelay: 6 },
        { enemyType: 'poisonshroom', count: 6, interval: 2.5, startDelay: 18 },
      ],
    },
  ],
};

const FUNGI_FORESTRY: LevelDef = {
  id: 113,
  chapter: 2,
  episode: 'III',
  name: 'Fungi Forestry',
  subtitle: 'The Fungi Forest',
  background: 'bg-fungiforestry',
  // Giant mushrooms of every colour stand in for trees round the edge. The
  // chapter's longest route: in low on the left, six switchback legs between
  // y 180 and 560, out high on the right.
  path: [
    { x: 0, y: 520 },
    { x: 200, y: 520 },
    { x: 200, y: 180 },
    { x: 400, y: 180 },
    { x: 400, y: 560 },
    { x: 600, y: 560 },
    { x: 600, y: 180 },
    { x: 800, y: 180 },
    { x: 800, y: 560 },
    { x: 960, y: 560 },
    { x: 960, y: 300 },
    { x: 1040, y: 300 },
  ],
  plots: [
    { id: 0, pos: { x: 100, y: 420 } },
    { id: 1, pos: { x: 300, y: 280 } },
    { id: 2, pos: { x: 300, y: 400 } },
    { id: 3, pos: { x: 300, y: 500 } },
    { id: 4, pos: { x: 500, y: 250 } },
    { id: 5, pos: { x: 500, y: 370 } },
    { id: 6, pos: { x: 500, y: 480 } },
    { id: 7, pos: { x: 700, y: 290 } },
    { id: 8, pos: { x: 700, y: 410 } },
    { id: 9, pos: { x: 700, y: 520 } },
    { id: 10, pos: { x: 880, y: 200 } },
    { id: 11, pos: { x: 880, y: 420 } },
    { id: 12, pos: { x: 1010, y: 180 } },
    { id: 13, pos: { x: 1010, y: 420 } },
  ],
  startCash: 2400,
  startLives: 5,
  coinReward: 350,
  expReward: 650,
  towerHp: HUB_TOWER_HP,
  waves: [
    // 1 — four Redshrooms.
    {
      groups: [{ enemyType: 'redshroom', count: 4, interval: 3, startDelay: 0 }],
    },
    // 2 — poison first.
    {
      groups: [
        { enemyType: 'poisonshroom', count: 2, interval: 4, startDelay: 0 },
        { enemyType: 'redshroom', count: 2, interval: 3, startDelay: 4 },
      ],
    },
    // 3 — two skulls.
    {
      groups: [
        { enemyType: 'skullshroom', count: 2, interval: 5, startDelay: 0 },
        { enemyType: 'redshroom', count: 3, interval: 3, startDelay: 4 },
      ],
    },
    // 4 — six red and two green.
    {
      groups: [
        { enemyType: 'redshroom', count: 6, interval: 2.5, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 2, interval: 4, startDelay: 6 },
      ],
    },
    // 5 — skulls and poison.
    {
      groups: [
        { enemyType: 'skullshroom', count: 3, interval: 4, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 2, interval: 4, startDelay: 6 },
      ],
    },
    // 6 — eight Redshrooms.
    {
      groups: [
        { enemyType: 'redshroom', count: 8, interval: 2, startDelay: 0 },
        { enemyType: 'skullshroom', count: 2, interval: 5, startDelay: 8 },
      ],
    },
    // 7 — five Poison Shrooms.
    {
      groups: [
        { enemyType: 'poisonshroom', count: 5, interval: 3, startDelay: 0 },
        { enemyType: 'redshroom', count: 4, interval: 2.5, startDelay: 6 },
      ],
    },
    // 8 — four skulls.
    {
      groups: [
        { enemyType: 'skullshroom', count: 4, interval: 3.5, startDelay: 0 },
        { enemyType: 'redshroom', count: 5, interval: 2.5, startDelay: 6 },
      ],
    },
    // 9 — ten Redshrooms.
    {
      groups: [
        { enemyType: 'redshroom', count: 10, interval: 2, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 3, interval: 4, startDelay: 8 },
      ],
    },
    // 10 — skulls and poison together.
    {
      groups: [
        { enemyType: 'skullshroom', count: 5, interval: 3, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 4, interval: 3, startDelay: 6 },
      ],
    },
    // 11 — all three, one after another.
    {
      groups: [
        { enemyType: 'redshroom', count: 8, interval: 2, startDelay: 0 },
        { enemyType: 'skullshroom', count: 3, interval: 4, startDelay: 6 },
        { enemyType: 'poisonshroom', count: 3, interval: 4, startDelay: 14 },
      ],
    },
    // 12 — six Poison Shrooms.
    {
      groups: [
        { enemyType: 'poisonshroom', count: 6, interval: 2.5, startDelay: 0 },
        { enemyType: 'skullshroom', count: 4, interval: 3.5, startDelay: 6 },
      ],
    },
    // 13 — a dozen Redshrooms.
    {
      groups: [
        { enemyType: 'redshroom', count: 12, interval: 1.8, startDelay: 0 },
        { enemyType: 'skullshroom', count: 3, interval: 4, startDelay: 10 },
      ],
    },
    // 14 — six skulls.
    {
      groups: [
        { enemyType: 'skullshroom', count: 6, interval: 3, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 5, interval: 3, startDelay: 8 },
      ],
    },
    // 15 — the colours of the forest.
    {
      groups: [
        { enemyType: 'redshroom', count: 10, interval: 2, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 5, interval: 2.5, startDelay: 6 },
        { enemyType: 'skullshroom', count: 3, interval: 4, startDelay: 14 },
      ],
    },
    // 16 — eight skulls.
    {
      groups: [
        { enemyType: 'skullshroom', count: 8, interval: 2.5, startDelay: 0 },
        { enemyType: 'redshroom', count: 8, interval: 2, startDelay: 8 },
      ],
    },
    // 17 — eight Poison Shrooms.
    {
      groups: [
        { enemyType: 'poisonshroom', count: 8, interval: 2.5, startDelay: 0 },
        { enemyType: 'skullshroom', count: 5, interval: 3, startDelay: 8 },
        { enemyType: 'redshroom', count: 6, interval: 2, startDelay: 16 },
      ],
    },
    // 18 — fourteen Redshrooms.
    {
      groups: [
        { enemyType: 'redshroom', count: 14, interval: 1.6, startDelay: 0 },
        { enemyType: 'skullshroom', count: 6, interval: 3, startDelay: 8 },
      ],
    },
    // 19 — the deep forest stirs.
    {
      groups: [
        { enemyType: 'skullshroom', count: 8, interval: 2.5, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 6, interval: 2.5, startDelay: 6 },
        { enemyType: 'redshroom', count: 10, interval: 1.8, startDelay: 14 },
      ],
    },
    // 20 — every shroom in the forest.
    {
      groups: [
        { enemyType: 'redshroom', count: 12, interval: 1.6, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 8, interval: 2.5, startDelay: 6 },
        { enemyType: 'skullshroom', count: 10, interval: 2.5, startDelay: 14 },
        { enemyType: 'redshroom', count: 10, interval: 1.6, startDelay: 30 },
      ],
    },
  ],
};

// ── Levels 4–6 (spec 2026-10-22-fungi-forest-levels-4-6-design.md) ──────────

/**
 * Mushy Mayhem's mushroom statue "at the top": [x, y, size]. Placed by the
 * game above the track (decor `mushystatue`), not baked into bg-mushymayhem;
 * hubLevels.test keeps it 80px off the trail and 90px off every plot.
 */
export const MUSHY_STATUE: readonly [number, number, number] = [640, 110, 150];

const POISON_TURF: LevelDef = {
  id: 114,
  chapter: 2,
  episode: 'IV',
  name: 'Poison Turf',
  subtitle: 'The Fungi Forest',
  background: 'bg-poisonturf',
  // Huge purple mushrooms crowd the corners. Three long legs across the
  // open turf, in high on the left and out low on the right; the Purple
  // Fungi debut on wave 3 and seal placement for 2 s every 5 s each.
  path: [
    { x: 0, y: 220 },
    { x: 880, y: 220 },
    { x: 880, y: 380 },
    { x: 200, y: 380 },
    { x: 200, y: 540 },
    { x: 1040, y: 540 },
  ],
  plots: [
    { id: 0, pos: { x: 300, y: 300 } },
    { id: 1, pos: { x: 450, y: 300 } },
    { id: 2, pos: { x: 600, y: 300 } },
    { id: 3, pos: { x: 750, y: 300 } },
    { id: 4, pos: { x: 330, y: 460 } },
    { id: 5, pos: { x: 480, y: 460 } },
    { id: 6, pos: { x: 630, y: 460 } },
    { id: 7, pos: { x: 780, y: 460 } },
    { id: 8, pos: { x: 610, y: 125 } },
    { id: 9, pos: { x: 750, y: 125 } },
    { id: 10, pos: { x: 970, y: 300 } },
    { id: 11, pos: { x: 970, y: 440 } },
    { id: 12, pos: { x: 110, y: 300 } },
  ],
  startCash: 2600,
  startLives: 5,
  coinReward: 375,
  expReward: 700,
  towerHp: HUB_TOWER_HP,
  waves: [
    // 1 — Redshrooms and a Poison Shroom to start.
    {
      groups: [
        { enemyType: 'redshroom', count: 4, interval: 3, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 1, interval: 1, startDelay: 8 },
      ],
    },
    // 2 — Skullshrooms behind a red line.
    {
      groups: [
        { enemyType: 'redshroom', count: 4, interval: 3, startDelay: 0 },
        { enemyType: 'skullshroom', count: 2, interval: 5, startDelay: 6 },
      ],
    },
    // 3 — the first Purple Fungi: build BEFORE he seals.
    {
      groups: [
        { enemyType: 'purplefungi', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'redshroom', count: 3, interval: 3, startDelay: 4 },
      ],
    },
    // 4 — two Purple Fungi.
    {
      groups: [
        { enemyType: 'purplefungi', count: 2, interval: 5, startDelay: 0 },
        { enemyType: 'redshroom', count: 4, interval: 3, startDelay: 4 },
      ],
    },
    // 5 — poison under the seal.
    {
      groups: [
        { enemyType: 'poisonshroom', count: 3, interval: 4, startDelay: 0 },
        { enemyType: 'purplefungi', count: 2, interval: 5, startDelay: 6 },
      ],
    },
    // 6 — a red rush.
    {
      groups: [
        { enemyType: 'redshroom', count: 8, interval: 2, startDelay: 0 },
        { enemyType: 'skullshroom', count: 2, interval: 5, startDelay: 8 },
      ],
    },
    // 7 — three Purple Fungi.
    {
      groups: [
        { enemyType: 'purplefungi', count: 3, interval: 4, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 2, interval: 4, startDelay: 6 },
        { enemyType: 'redshroom', count: 4, interval: 2.5, startDelay: 12 },
      ],
    },
    // 8 — Skullshrooms in force.
    {
      groups: [
        { enemyType: 'skullshroom', count: 4, interval: 3.5, startDelay: 0 },
        { enemyType: 'purplefungi', count: 2, interval: 5, startDelay: 6 },
      ],
    },
    // 9 — a mixed patrol.
    {
      groups: [
        { enemyType: 'redshroom', count: 6, interval: 2.5, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 3, interval: 3.5, startDelay: 6 },
        { enemyType: 'purplefungi', count: 2, interval: 5, startDelay: 12 },
      ],
    },
    // 10 — four Purple Fungi: the seal is up as often as it is down.
    {
      groups: [
        { enemyType: 'purplefungi', count: 4, interval: 3.5, startDelay: 0 },
        { enemyType: 'skullshroom', count: 3, interval: 4, startDelay: 8 },
      ],
    },
    // 11 — a breather of Redshrooms.
    {
      groups: [{ enemyType: 'redshroom', count: 10, interval: 2, startDelay: 0 }],
    },
    // 12 — poison and skulls.
    {
      groups: [
        { enemyType: 'poisonshroom', count: 5, interval: 3, startDelay: 0 },
        { enemyType: 'skullshroom', count: 4, interval: 3.5, startDelay: 8 },
      ],
    },
    // 13 — five Purple Fungi.
    {
      groups: [
        { enemyType: 'purplefungi', count: 5, interval: 3, startDelay: 0 },
        { enemyType: 'redshroom', count: 6, interval: 2, startDelay: 6 },
        { enemyType: 'poisonshroom', count: 2, interval: 4, startDelay: 14 },
      ],
    },
    // 14 — the turf thickens.
    {
      groups: [
        { enemyType: 'skullshroom', count: 5, interval: 3, startDelay: 0 },
        { enemyType: 'purplefungi', count: 3, interval: 4, startDelay: 6 },
        { enemyType: 'redshroom', count: 6, interval: 2, startDelay: 14 },
      ],
    },
    // 15 — a poison flood.
    {
      groups: [
        { enemyType: 'poisonshroom', count: 7, interval: 2.5, startDelay: 0 },
        { enemyType: 'purplefungi', count: 3, interval: 4, startDelay: 8 },
      ],
    },
    // 16 — six Skullshrooms.
    {
      groups: [
        { enemyType: 'skullshroom', count: 6, interval: 3, startDelay: 0 },
        { enemyType: 'redshroom', count: 8, interval: 1.8, startDelay: 8 },
        { enemyType: 'purplefungi', count: 2, interval: 5, startDelay: 16 },
      ],
    },
    // 17 — purple everywhere.
    {
      groups: [
        { enemyType: 'purplefungi', count: 7, interval: 2.5, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 4, interval: 3, startDelay: 8 },
      ],
    },
    // 18 — every shroom in turn.
    {
      groups: [
        { enemyType: 'redshroom', count: 10, interval: 1.6, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 5, interval: 3, startDelay: 6 },
        { enemyType: 'skullshroom', count: 5, interval: 3, startDelay: 12 },
        { enemyType: 'purplefungi', count: 3, interval: 4, startDelay: 18 },
      ],
    },
    // 19 — skulls and seals.
    {
      groups: [
        { enemyType: 'skullshroom', count: 8, interval: 2.5, startDelay: 0 },
        { enemyType: 'purplefungi', count: 5, interval: 3, startDelay: 8 },
      ],
    },
    // 20 — the deep turf stirs.
    {
      groups: [
        { enemyType: 'purplefungi', count: 6, interval: 2.5, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 6, interval: 2.5, startDelay: 6 },
        { enemyType: 'skullshroom', count: 6, interval: 2.5, startDelay: 14 },
      ],
    },
    // 21 — the whole Poison Turf.
    {
      groups: [
        { enemyType: 'redshroom', count: 12, interval: 1.5, startDelay: 0 },
        { enemyType: 'purplefungi', count: 8, interval: 2.5, startDelay: 6 },
        { enemyType: 'poisonshroom', count: 8, interval: 2.5, startDelay: 14 },
        { enemyType: 'skullshroom', count: 8, interval: 2.5, startDelay: 22 },
      ],
    },
  ],
};

const MUSHY_MAYHEM: LevelDef = {
  id: 115,
  chapter: 2,
  episode: 'V',
  name: 'Mushy Mayhem',
  subtitle: 'The Fungi Forest',
  background: 'bg-mushymayhem',
  // A clearing full of brown mushrooms with the Mushy's statue at the top
  // (MUSHY_STATUE, placed by the game). The route keeps well below it.
  // The Mushy comes ALONE on wave 23: a boss, so a leak ends the run.
  path: [
    { x: 0, y: 460 },
    { x: 180, y: 460 },
    { x: 180, y: 200 },
    { x: 420, y: 200 },
    { x: 420, y: 560 },
    { x: 860, y: 560 },
    { x: 860, y: 220 },
    { x: 1040, y: 220 },
  ],
  plots: [
    { id: 0, pos: { x: 90, y: 320 } },
    { id: 1, pos: { x: 300, y: 300 } },
    { id: 2, pos: { x: 300, y: 440 } },
    { id: 3, pos: { x: 520, y: 290 } },
    { id: 4, pos: { x: 640, y: 290 } },
    { id: 5, pos: { x: 760, y: 290 } },
    { id: 6, pos: { x: 520, y: 430 } },
    { id: 7, pos: { x: 640, y: 430 } },
    { id: 8, pos: { x: 760, y: 430 } },
    { id: 9, pos: { x: 960, y: 330 } },
    { id: 10, pos: { x: 960, y: 470 } },
    { id: 11, pos: { x: 640, y: 650 } },
    { id: 12, pos: { x: 200, y: 580 } },
  ],
  decor: 'mushystatue',
  startCash: 2800,
  startLives: 5,
  coinReward: 400,
  expReward: 750,
  towerHp: HUB_TOWER_HP,
  waves: [
    // 1 — Redshrooms in the clearing.
    {
      groups: [{ enemyType: 'redshroom', count: 5, interval: 2.5, startDelay: 0 }],
    },
    // 2 — poison and red.
    {
      groups: [
        { enemyType: 'poisonshroom', count: 2, interval: 4, startDelay: 0 },
        { enemyType: 'redshroom', count: 4, interval: 2.5, startDelay: 4 },
      ],
    },
    // 3 — skulls under the statue.
    {
      groups: [
        { enemyType: 'skullshroom', count: 3, interval: 4, startDelay: 0 },
        { enemyType: 'redshroom', count: 4, interval: 2.5, startDelay: 6 },
      ],
    },
    // 4 — the Purple Fungi return.
    {
      groups: [
        { enemyType: 'purplefungi', count: 2, interval: 5, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 2, interval: 4, startDelay: 6 },
      ],
    },
    // 5 — a red rush.
    {
      groups: [
        { enemyType: 'redshroom', count: 9, interval: 2, startDelay: 0 },
        { enemyType: 'purplefungi', count: 1, interval: 1, startDelay: 10 },
      ],
    },
    // 6 — skulls and seals.
    {
      groups: [
        { enemyType: 'skullshroom', count: 4, interval: 3.5, startDelay: 0 },
        { enemyType: 'purplefungi', count: 2, interval: 5, startDelay: 6 },
      ],
    },
    // 7 — a poison line.
    {
      groups: [
        { enemyType: 'poisonshroom', count: 5, interval: 3, startDelay: 0 },
        { enemyType: 'redshroom', count: 5, interval: 2, startDelay: 8 },
      ],
    },
    // 8 — every shroom, one by one.
    {
      groups: [
        { enemyType: 'redshroom', count: 4, interval: 2.5, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 3, interval: 3.5, startDelay: 6 },
        { enemyType: 'skullshroom', count: 3, interval: 4, startDelay: 12 },
        { enemyType: 'purplefungi', count: 2, interval: 5, startDelay: 18 },
      ],
    },
    // 9 — four Purple Fungi.
    {
      groups: [
        { enemyType: 'purplefungi', count: 4, interval: 3.5, startDelay: 0 },
        { enemyType: 'skullshroom', count: 3, interval: 4, startDelay: 8 },
      ],
    },
    // 10 — a skull wall.
    {
      groups: [
        { enemyType: 'skullshroom', count: 6, interval: 3, startDelay: 0 },
        { enemyType: 'redshroom', count: 6, interval: 2, startDelay: 8 },
      ],
    },
    // 11 — a breather of Redshrooms.
    {
      groups: [{ enemyType: 'redshroom', count: 12, interval: 1.8, startDelay: 0 }],
    },
    // 12 — poison and purple.
    {
      groups: [
        { enemyType: 'poisonshroom', count: 6, interval: 2.5, startDelay: 0 },
        { enemyType: 'purplefungi', count: 4, interval: 3.5, startDelay: 6 },
      ],
    },
    // 13 — the brown woods thicken.
    {
      groups: [
        { enemyType: 'skullshroom', count: 6, interval: 3, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 4, interval: 3, startDelay: 6 },
        { enemyType: 'redshroom', count: 6, interval: 2, startDelay: 14 },
      ],
    },
    // 14 — five Purple Fungi.
    {
      groups: [
        { enemyType: 'purplefungi', count: 5, interval: 3, startDelay: 0 },
        { enemyType: 'redshroom', count: 8, interval: 1.8, startDelay: 6 },
      ],
    },
    // 15 — a skull march.
    {
      groups: [
        { enemyType: 'skullshroom', count: 8, interval: 2.5, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 4, interval: 3, startDelay: 10 },
      ],
    },
    // 16 — red, poison, red.
    {
      groups: [
        { enemyType: 'redshroom', count: 10, interval: 1.6, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 6, interval: 2.5, startDelay: 6 },
        { enemyType: 'redshroom', count: 8, interval: 1.6, startDelay: 16 },
      ],
    },
    // 17 — seals and skulls.
    {
      groups: [
        { enemyType: 'purplefungi', count: 6, interval: 2.5, startDelay: 0 },
        { enemyType: 'skullshroom', count: 6, interval: 2.5, startDelay: 8 },
      ],
    },
    // 18 — a poison flood.
    {
      groups: [
        { enemyType: 'poisonshroom', count: 9, interval: 2.2, startDelay: 0 },
        { enemyType: 'purplefungi', count: 4, interval: 3, startDelay: 8 },
      ],
    },
    // 19 — every shroom at once.
    {
      groups: [
        { enemyType: 'redshroom', count: 10, interval: 1.6, startDelay: 0 },
        { enemyType: 'skullshroom', count: 6, interval: 2.5, startDelay: 4 },
        { enemyType: 'poisonshroom', count: 6, interval: 2.5, startDelay: 10 },
        { enemyType: 'purplefungi', count: 4, interval: 3, startDelay: 16 },
      ],
    },
    // 20 — the forest gathers.
    {
      groups: [
        { enemyType: 'skullshroom', count: 10, interval: 2.2, startDelay: 0 },
        { enemyType: 'purplefungi', count: 6, interval: 2.5, startDelay: 8 },
      ],
    },
    // 21 — the ground shakes.
    {
      groups: [
        { enemyType: 'redshroom', count: 14, interval: 1.4, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 8, interval: 2.2, startDelay: 6 },
        { enemyType: 'skullshroom', count: 8, interval: 2.2, startDelay: 14 },
      ],
    },
    // 22 — the last guard before the boss.
    {
      groups: [
        { enemyType: 'purplefungi', count: 8, interval: 2.2, startDelay: 0 },
        { enemyType: 'skullshroom', count: 10, interval: 2.2, startDelay: 6 },
        { enemyType: 'poisonshroom', count: 8, interval: 2.2, startDelay: 14 },
        { enemyType: 'redshroom', count: 12, interval: 1.4, startDelay: 22 },
      ],
    },
    // 23 — THE MUSHY, alone.
    {
      groups: [{ enemyType: 'themushy', count: 1, interval: 1, startDelay: 0 }],
    },
  ],
};

const GLOWING_FOREST: LevelDef = {
  id: 116,
  chapter: 2,
  episode: 'VI',
  name: 'Glowing Forest',
  subtitle: 'The Fungi Forest',
  background: 'bg-glowingforest',
  // Tall dusk trees with small glowing orange mushrooms at their roots ring a
  // dark clearing. In on the left, three deep switchbacks between y 150 and
  // 470 (the trees close in below that), out on the right. NO Redshroom and
  // NO Poison Shroom here (the brief): Skullshrooms, Purple Fungi and the new
  // Glowshroom only.
  path: [
    { x: 0, y: 250 },
    { x: 260, y: 250 },
    { x: 260, y: 470 },
    { x: 500, y: 470 },
    { x: 500, y: 150 },
    { x: 740, y: 150 },
    { x: 740, y: 470 },
    { x: 940, y: 470 },
    { x: 940, y: 250 },
    { x: 1040, y: 250 },
  ],
  plots: [
    { id: 0, pos: { x: 150, y: 370 } },
    { id: 1, pos: { x: 380, y: 200 } },
    { id: 2, pos: { x: 380, y: 330 } },
    { id: 3, pos: { x: 620, y: 250 } },
    { id: 4, pos: { x: 620, y: 380 } },
    { id: 5, pos: { x: 620, y: 560 } },
    { id: 6, pos: { x: 840, y: 330 } },
    { id: 7, pos: { x: 840, y: 190 } },
    { id: 8, pos: { x: 1010, y: 380 } },
    { id: 9, pos: { x: 620, y: 90 } },
    { id: 10, pos: { x: 500, y: 590 } },
    { id: 11, pos: { x: 740, y: 590 } },
  ],
  startCash: 3000,
  startLives: 5,
  coinReward: 425,
  expReward: 800,
  towerHp: HUB_TOWER_HP,
  waves: [
    // 1 — Skullshrooms in the dusk.
    {
      groups: [{ enemyType: 'skullshroom', count: 3, interval: 4, startDelay: 0 }],
    },
    // 2 — Purple Fungi behind them.
    {
      groups: [
        { enemyType: 'skullshroom', count: 3, interval: 4, startDelay: 0 },
        { enemyType: 'purplefungi', count: 1, interval: 1, startDelay: 8 },
      ],
    },
    // 3 — the first Glowshroom: a potion turns a tower on its friends.
    {
      groups: [
        { enemyType: 'glowshroom', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'skullshroom', count: 2, interval: 5, startDelay: 4 },
      ],
    },
    // 4 — two Glowshrooms.
    {
      groups: [
        { enemyType: 'glowshroom', count: 2, interval: 5, startDelay: 0 },
        { enemyType: 'purplefungi', count: 2, interval: 5, startDelay: 4 },
      ],
    },
    // 5 — a skull line.
    {
      groups: [
        { enemyType: 'skullshroom', count: 5, interval: 3, startDelay: 0 },
        { enemyType: 'glowshroom', count: 1, interval: 1, startDelay: 8 },
      ],
    },
    // 6 — glow and seals.
    {
      groups: [
        { enemyType: 'glowshroom', count: 3, interval: 4, startDelay: 0 },
        { enemyType: 'purplefungi', count: 2, interval: 5, startDelay: 6 },
      ],
    },
    // 7 — purple first.
    {
      groups: [
        { enemyType: 'purplefungi', count: 4, interval: 3.5, startDelay: 0 },
        { enemyType: 'skullshroom', count: 3, interval: 4, startDelay: 6 },
      ],
    },
    // 8 — three Glowshrooms in a row.
    {
      groups: [
        { enemyType: 'glowshroom', count: 3, interval: 3.5, startDelay: 0 },
        { enemyType: 'skullshroom', count: 4, interval: 3, startDelay: 6 },
      ],
    },
    // 9 — a patrol of all three.
    {
      groups: [
        { enemyType: 'skullshroom', count: 4, interval: 3, startDelay: 0 },
        { enemyType: 'purplefungi', count: 3, interval: 4, startDelay: 6 },
        { enemyType: 'glowshroom', count: 2, interval: 4, startDelay: 12 },
      ],
    },
    // 10 — four Glowshrooms.
    {
      groups: [
        { enemyType: 'glowshroom', count: 4, interval: 3.5, startDelay: 0 },
        { enemyType: 'purplefungi', count: 2, interval: 5, startDelay: 8 },
      ],
    },
    // 11 — a skull wall.
    {
      groups: [
        { enemyType: 'skullshroom', count: 7, interval: 2.5, startDelay: 0 },
        { enemyType: 'glowshroom', count: 2, interval: 4, startDelay: 10 },
      ],
    },
    // 12 — the glow spreads.
    {
      groups: [
        { enemyType: 'glowshroom', count: 5, interval: 3, startDelay: 0 },
        { enemyType: 'skullshroom', count: 4, interval: 3, startDelay: 8 },
      ],
    },
    // 13 — five Purple Fungi.
    {
      groups: [
        { enemyType: 'purplefungi', count: 5, interval: 3, startDelay: 0 },
        { enemyType: 'glowshroom', count: 3, interval: 3.5, startDelay: 8 },
      ],
    },
    // 14 — a breather of Skullshrooms.
    {
      groups: [{ enemyType: 'skullshroom', count: 9, interval: 2.2, startDelay: 0 }],
    },
    // 15 — glow behind a purple screen.
    {
      groups: [
        { enemyType: 'purplefungi', count: 4, interval: 3, startDelay: 0 },
        { enemyType: 'glowshroom', count: 5, interval: 3, startDelay: 6 },
      ],
    },
    // 16 — six Glowshrooms.
    {
      groups: [
        { enemyType: 'glowshroom', count: 6, interval: 2.8, startDelay: 0 },
        { enemyType: 'skullshroom', count: 5, interval: 2.8, startDelay: 8 },
      ],
    },
    // 17 — skulls and seals.
    {
      groups: [
        { enemyType: 'skullshroom', count: 8, interval: 2.4, startDelay: 0 },
        { enemyType: 'purplefungi', count: 5, interval: 3, startDelay: 8 },
      ],
    },
    // 18 — the forest lights up.
    {
      groups: [
        { enemyType: 'glowshroom', count: 7, interval: 2.6, startDelay: 0 },
        { enemyType: 'purplefungi', count: 4, interval: 3, startDelay: 6 },
        { enemyType: 'skullshroom', count: 5, interval: 2.8, startDelay: 14 },
      ],
    },
    // 19 — a purple flood.
    {
      groups: [
        { enemyType: 'purplefungi', count: 8, interval: 2.4, startDelay: 0 },
        { enemyType: 'glowshroom', count: 4, interval: 3, startDelay: 10 },
      ],
    },
    // 20 — a skull march.
    {
      groups: [
        { enemyType: 'skullshroom', count: 10, interval: 2.2, startDelay: 0 },
        { enemyType: 'glowshroom', count: 5, interval: 3, startDelay: 8 },
      ],
    },
    // 21 — every shroom of the Glowing Forest.
    {
      groups: [
        { enemyType: 'glowshroom', count: 6, interval: 2.5, startDelay: 0 },
        { enemyType: 'skullshroom', count: 8, interval: 2.2, startDelay: 6 },
        { enemyType: 'purplefungi', count: 6, interval: 2.5, startDelay: 14 },
      ],
    },
    // 22 — eight Glowshrooms.
    {
      groups: [
        { enemyType: 'glowshroom', count: 8, interval: 2.4, startDelay: 0 },
        { enemyType: 'purplefungi', count: 6, interval: 2.5, startDelay: 8 },
      ],
    },
    // 23 — the deep forest stirs.
    {
      groups: [
        { enemyType: 'skullshroom', count: 12, interval: 2, startDelay: 0 },
        { enemyType: 'glowshroom', count: 7, interval: 2.4, startDelay: 6 },
        { enemyType: 'purplefungi', count: 6, interval: 2.5, startDelay: 16 },
      ],
    },
    // 24 — the whole Glowing Forest.
    {
      groups: [
        { enemyType: 'glowshroom', count: 10, interval: 2.2, startDelay: 0 },
        { enemyType: 'skullshroom', count: 12, interval: 2, startDelay: 6 },
        { enemyType: 'purplefungi', count: 8, interval: 2.4, startDelay: 16 },
        { enemyType: 'glowshroom', count: 6, interval: 2.4, startDelay: 30 },
      ],
    },
  ],
};

const ICY_PHOTOSYNTHESIS: LevelDef = {
  id: 117,
  chapter: 2,
  episode: 'VII',
  name: 'Icy Photosynthesis',
  subtitle: 'The Fungi Forest',
  background: 'bg-icyphotosynthesis',
  // A frozen mushroom forest: snow and ice on the floor, snow-capped
  // mushrooms hung with icicles around the edges. In on the left at y 340,
  // three switchbacks between y 160 and 500 (the mushrooms close in above
  // and below), out on the right. Every Fungi Forest shroom but The Mushy,
  // and the new Blizzard Fungi from wave 3.
  path: [
    { x: 0, y: 340 },
    { x: 240, y: 340 },
    { x: 240, y: 160 },
    { x: 480, y: 160 },
    { x: 480, y: 500 },
    { x: 720, y: 500 },
    { x: 720, y: 160 },
    { x: 940, y: 160 },
    { x: 940, y: 420 },
    { x: 1040, y: 420 },
  ],
  plots: [
    { id: 0, pos: { x: 360, y: 260 } },
    { id: 1, pos: { x: 360, y: 390 } },
    { id: 2, pos: { x: 600, y: 260 } },
    { id: 3, pos: { x: 600, y: 400 } },
    { id: 4, pos: { x: 830, y: 260 } },
    { id: 5, pos: { x: 830, y: 370 } },
    { id: 6, pos: { x: 680, y: 600 } },
    { id: 7, pos: { x: 600, y: 90 } },
    { id: 8, pos: { x: 1020, y: 300 } },
    { id: 9, pos: { x: 300, y: 480 } },
    { id: 10, pos: { x: 850, y: 490 } },
    { id: 11, pos: { x: 1010, y: 170 } },
  ],
  startCash: 3200,
  startLives: 5,
  coinReward: 450,
  expReward: 850,
  towerHp: HUB_TOWER_HP,
  waves: [
    // 1 — Redshrooms on the snow.
    {
      groups: [{ enemyType: 'redshroom', count: 5, interval: 3, startDelay: 0 }],
    },
    // 2 — Poison under the frost.
    {
      groups: [
        { enemyType: 'redshroom', count: 4, interval: 3, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 2, interval: 4, startDelay: 8 },
      ],
    },
    // 3 — the first Blizzard Fungi: snowballs, and a tenth of your shots miss.
    {
      groups: [
        { enemyType: 'blizzardfungi', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'redshroom', count: 3, interval: 3, startDelay: 4 },
      ],
    },
    // 4 — two Blizzard Fungi.
    {
      groups: [
        { enemyType: 'blizzardfungi', count: 2, interval: 5, startDelay: 0 },
        { enemyType: 'skullshroom', count: 2, interval: 5, startDelay: 4 },
      ],
    },
    // 5 — a seal on the ice.
    {
      groups: [
        { enemyType: 'purplefungi', count: 2, interval: 5, startDelay: 0 },
        { enemyType: 'redshroom', count: 5, interval: 2.5, startDelay: 4 },
        { enemyType: 'blizzardfungi', count: 1, interval: 1, startDelay: 12 },
      ],
    },
    // 6 — the glow under the snow.
    {
      groups: [
        { enemyType: 'glowshroom', count: 2, interval: 5, startDelay: 0 },
        { enemyType: 'blizzardfungi', count: 2, interval: 5, startDelay: 6 },
      ],
    },
    // 7 — a skull line.
    {
      groups: [
        { enemyType: 'skullshroom', count: 5, interval: 3, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 3, interval: 3.5, startDelay: 8 },
      ],
    },
    // 8 — three Blizzard Fungi in a row.
    {
      groups: [
        { enemyType: 'blizzardfungi', count: 3, interval: 4, startDelay: 0 },
        { enemyType: 'redshroom', count: 6, interval: 2, startDelay: 6 },
      ],
    },
    // 9 — every Fungi of the chapter so far.
    {
      groups: [
        { enemyType: 'redshroom', count: 4, interval: 2.5, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 3, interval: 3, startDelay: 6 },
        { enemyType: 'skullshroom', count: 3, interval: 3.5, startDelay: 12 },
        { enemyType: 'purplefungi', count: 2, interval: 4, startDelay: 18 },
      ],
    },
    // 10 — a blizzard.
    {
      groups: [
        { enemyType: 'blizzardfungi', count: 4, interval: 3.5, startDelay: 0 },
        { enemyType: 'glowshroom', count: 2, interval: 4, startDelay: 8 },
      ],
    },
    // 11 — poison and seals.
    {
      groups: [
        { enemyType: 'poisonshroom', count: 5, interval: 3, startDelay: 0 },
        { enemyType: 'purplefungi', count: 3, interval: 3.5, startDelay: 8 },
      ],
    },
    // 12 — the glow spreads.
    {
      groups: [
        { enemyType: 'glowshroom', count: 4, interval: 3.5, startDelay: 0 },
        { enemyType: 'skullshroom', count: 4, interval: 3, startDelay: 8 },
      ],
    },
    // 13 — five Blizzard Fungi.
    {
      groups: [
        { enemyType: 'blizzardfungi', count: 5, interval: 3, startDelay: 0 },
        { enemyType: 'redshroom', count: 8, interval: 1.8, startDelay: 8 },
      ],
    },
    // 14 — a breather of Redshrooms.
    {
      groups: [{ enemyType: 'redshroom', count: 12, interval: 1.5, startDelay: 0 }],
    },
    // 15 — ice behind a purple screen.
    {
      groups: [
        { enemyType: 'purplefungi', count: 4, interval: 3, startDelay: 0 },
        { enemyType: 'blizzardfungi', count: 5, interval: 3, startDelay: 6 },
      ],
    },
    // 16 — a skull march with poison.
    {
      groups: [
        { enemyType: 'skullshroom', count: 7, interval: 2.6, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 5, interval: 2.8, startDelay: 8 },
      ],
    },
    // 17 — six Blizzard Fungi.
    {
      groups: [
        { enemyType: 'blizzardfungi', count: 6, interval: 2.8, startDelay: 0 },
        { enemyType: 'glowshroom', count: 3, interval: 3.5, startDelay: 8 },
      ],
    },
    // 18 — glow and frost.
    {
      groups: [
        { enemyType: 'glowshroom', count: 5, interval: 3, startDelay: 0 },
        { enemyType: 'blizzardfungi', count: 4, interval: 3, startDelay: 6 },
        { enemyType: 'purplefungi', count: 3, interval: 3.5, startDelay: 14 },
      ],
    },
    // 19 — the forest freezes over.
    {
      groups: [
        { enemyType: 'blizzardfungi', count: 7, interval: 2.6, startDelay: 0 },
        { enemyType: 'skullshroom', count: 6, interval: 2.6, startDelay: 8 },
        { enemyType: 'redshroom', count: 8, interval: 1.6, startDelay: 16 },
      ],
    },
    // 20 — a purple flood.
    {
      groups: [
        { enemyType: 'purplefungi', count: 7, interval: 2.5, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 6, interval: 2.6, startDelay: 8 },
      ],
    },
    // 21 — every shroom at once.
    {
      groups: [
        { enemyType: 'redshroom', count: 8, interval: 1.6, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 5, interval: 2.6, startDelay: 6 },
        { enemyType: 'skullshroom', count: 5, interval: 2.6, startDelay: 12 },
        { enemyType: 'purplefungi', count: 4, interval: 3, startDelay: 18 },
        { enemyType: 'glowshroom', count: 3, interval: 3, startDelay: 24 },
      ],
    },
    // 22 — eight Blizzard Fungi.
    {
      groups: [
        { enemyType: 'blizzardfungi', count: 8, interval: 2.4, startDelay: 0 },
        { enemyType: 'glowshroom', count: 5, interval: 2.8, startDelay: 8 },
      ],
    },
    // 23 — the deep freeze.
    {
      groups: [
        { enemyType: 'skullshroom', count: 10, interval: 2.2, startDelay: 0 },
        { enemyType: 'blizzardfungi', count: 7, interval: 2.4, startDelay: 6 },
        { enemyType: 'purplefungi', count: 5, interval: 2.8, startDelay: 16 },
      ],
    },
    // 24 — the glow and the cold.
    {
      groups: [
        { enemyType: 'glowshroom', count: 8, interval: 2.4, startDelay: 0 },
        { enemyType: 'blizzardfungi', count: 8, interval: 2.4, startDelay: 6 },
        { enemyType: 'poisonshroom', count: 6, interval: 2.4, startDelay: 16 },
      ],
    },
    // 25 — the whole frozen forest.
    {
      groups: [
        { enemyType: 'blizzardfungi', count: 10, interval: 2.2, startDelay: 0 },
        { enemyType: 'skullshroom', count: 10, interval: 2, startDelay: 6 },
        { enemyType: 'purplefungi', count: 6, interval: 2.4, startDelay: 14 },
        { enemyType: 'glowshroom', count: 6, interval: 2.4, startDelay: 22 },
        { enemyType: 'redshroom', count: 12, interval: 1.4, startDelay: 30 },
        { enemyType: 'poisonshroom', count: 6, interval: 2.4, startDelay: 34 },
      ],
    },
  ],
};

/**
 * Grave of the Rotten's mushroom graves — [x, y, size] — placed by the game
 * beside the track, clear of the trail and every plot (pinned by
 * hubLevels.test). Since 2026-10-29 they are bigger and sit only on BARE
 * ground: each spot was picked by scanning bg-graveoftherotten for patches
 * with no mushroom art under a 96 px box (the left half has none, so all
 * five stand in the middle and on the right).
 */
export const SHROOM_GRAVES: readonly [number, number, number][] = [
  [420, 605, 108],
  [560, 85, 100],
  [735, 90, 100],
  [1000, 370, 108],
  [740, 610, 108],
];

/**
 * Pathway of the Fungi's five moss-covered golden statues — [x, y, size] —
 * each with two clusters of glowing golden mushrooms at its feet.
 */
export const GOLDEN_STATUES: readonly [number, number, number][] = [
  [100, 230, 84],
  [580, 230, 84],
  [480, 410, 84],
  [1000, 410, 84],
  [620, 640, 84],
];

/** Magical Mayhem's glowing mushroom clusters — [x, y, size, blue?]. */
export const GLOW_CLUSTERS: readonly [number, number, number, boolean][] = [
  [310, 640, 52, true],
  [530, 100, 52, false],
  [750, 640, 52, true],
  [100, 250, 52, false],
  [960, 140, 52, true],
  [960, 660, 44, false],
];

/**
 * The Shroombellion's tree houses — [x, y, size] — the giant one first, then
 * the three small ones; rope bridges run from the giant one to each. Made
 * bigger on 2026-10-29 (giant 150 → 220, small 90 → 130/140), which moved
 * plots 16 and 17 down 30 px to clear the giant one.
 */
export const TREE_HOUSES: readonly [number, number, number][] = [
  [520, 365, 220],
  [405, 180, 130],
  [635, 180, 130],
  [520, 590, 140],
];

/**
 * The Shroombellion's vines — [x, length] — hung from the TOP EDGE of the
 * board and swaying. The long ones hang down the clear margins and between
 * the two top tree houses; the short ones stop above the first plot row, so a
 * vine never covers a tower (they draw under the plots anyway).
 */
export const SHROOMBELLION_VINES: readonly [number, number][] = [
  [28, 430],
  [140, 130],
  [260, 130],
  [520, 250],
  [780, 130],
  [900, 130],
  [1022, 450],
];

const GRAVE_OF_THE_ROTTEN: LevelDef = {
  id: 118,
  chapter: 2,
  episode: 'VIII',
  name: 'Grave of the Rotten',
  subtitle: 'The Fungi Forest',
  background: 'bg-graveoftherotten',
  // A dead forest: huge rotten brown mushrooms (and small ones round their
  // stems) at the edges, and mushroom graves the game places beside the track
  // (SHROOM_GRAVES). Three switchbacks left to right. Every Fungi shroom but
  // The Mushy, and the Zombieshroom from wave 2 — he can only stab a tower
  // within 100 px, so the plots beside the bends are the dangerous ones.
  path: [
    { x: 0, y: 200 },
    { x: 260, y: 200 },
    { x: 260, y: 520 },
    { x: 520, y: 520 },
    { x: 520, y: 180 },
    { x: 780, y: 180 },
    { x: 780, y: 500 },
    { x: 1040, y: 500 },
  ],
  plots: [
    { id: 0, pos: { x: 130, y: 300 } },
    { id: 1, pos: { x: 390, y: 330 } },
    { id: 2, pos: { x: 390, y: 440 } },
    { id: 3, pos: { x: 650, y: 300 } },
    { id: 4, pos: { x: 650, y: 410 } },
    { id: 5, pos: { x: 910, y: 300 } },
    { id: 6, pos: { x: 910, y: 410 } },
    { id: 7, pos: { x: 390, y: 100 } },
    { id: 8, pos: { x: 650, y: 600 } },
    { id: 9, pos: { x: 130, y: 620 } },
    { id: 10, pos: { x: 910, y: 620 } },
    { id: 11, pos: { x: 130, y: 100 } },
  ],
  decor: 'shroomgraves',
  startCash: 3300,
  startLives: 5,
  coinReward: 460,
  expReward: 870,
  towerHp: HUB_TOWER_HP,
  waves: [
    // 1 — the rot spreads.
    {
      groups: [{ enemyType: 'skullshroom', count: 4, interval: 3.2, startDelay: 0 }],
    },
    // 2 — the graves stir.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 1, interval: 3.9, startDelay: 0 },
        { enemyType: 'glowshroom', count: 4, interval: 3.1, startDelay: 6 },
      ],
    },
    // 3 — the rot spreads.
    {
      groups: [{ enemyType: 'redshroom', count: 4, interval: 3.1, startDelay: 0 }],
    },
    // 4 — the graves stir.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 1, interval: 3.8, startDelay: 0 },
        { enemyType: 'skullshroom', count: 4, interval: 3.0, startDelay: 6 },
      ],
    },
    // 5 — the rot spreads.
    {
      groups: [{ enemyType: 'glowshroom', count: 5, interval: 2.9, startDelay: 0 }],
    },
    // 6 — the graves stir.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 2, interval: 3.7, startDelay: 0 },
        { enemyType: 'redshroom', count: 5, interval: 2.9, startDelay: 6 },
      ],
    },
    // 7 — the rot spreads.
    {
      groups: [{ enemyType: 'skullshroom', count: 5, interval: 2.8, startDelay: 0 }],
    },
    // 8 — the graves stir.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 2, interval: 3.5, startDelay: 0 },
        { enemyType: 'glowshroom', count: 6, interval: 2.7, startDelay: 6 },
      ],
    },
    // 9 — the rot spreads.
    {
      groups: [
        { enemyType: 'redshroom', count: 6, interval: 2.7, startDelay: 0 },
        { enemyType: 'purplefungi', count: 5, interval: 2.7, startDelay: 13 },
      ],
    },
    // 10 — the graves stir.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 3, interval: 3.4, startDelay: 0 },
        { enemyType: 'skullshroom', count: 6, interval: 2.6, startDelay: 6 },
        { enemyType: 'blizzardfungi', count: 5, interval: 2.6, startDelay: 19 },
      ],
    },
    // 11 — the rot spreads.
    {
      groups: [
        { enemyType: 'glowshroom', count: 6, interval: 2.5, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 5, interval: 2.5, startDelay: 13 },
      ],
    },
    // 12 — the graves stir.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 3, interval: 3.3, startDelay: 0 },
        { enemyType: 'redshroom', count: 7, interval: 2.5, startDelay: 6 },
        { enemyType: 'purplefungi', count: 6, interval: 2.5, startDelay: 20 },
      ],
    },
    // 13 — the rot spreads.
    {
      groups: [
        { enemyType: 'skullshroom', count: 7, interval: 2.4, startDelay: 0 },
        { enemyType: 'blizzardfungi', count: 6, interval: 2.4, startDelay: 14 },
      ],
    },
    // 14 — the graves stir.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 4, interval: 3.1, startDelay: 0 },
        { enemyType: 'glowshroom', count: 7, interval: 2.3, startDelay: 6 },
        { enemyType: 'poisonshroom', count: 6, interval: 2.3, startDelay: 19 },
      ],
    },
    // 15 — the rot spreads.
    {
      groups: [
        { enemyType: 'redshroom', count: 8, interval: 2.3, startDelay: 0 },
        { enemyType: 'purplefungi', count: 7, interval: 2.3, startDelay: 15 },
      ],
    },
    // 16 — the graves stir.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 4, interval: 3.0, startDelay: 0 },
        { enemyType: 'skullshroom', count: 8, interval: 2.2, startDelay: 6 },
        { enemyType: 'blizzardfungi', count: 7, interval: 2.2, startDelay: 20 },
        { enemyType: 'skullshroom', count: 7, interval: 2.2, startDelay: 33 },
      ],
    },
    // 17 — the rot spreads.
    {
      groups: [
        { enemyType: 'glowshroom', count: 8, interval: 2.1, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 7, interval: 2.1, startDelay: 14 },
        { enemyType: 'glowshroom', count: 7, interval: 2.1, startDelay: 26 },
      ],
    },
    // 18 — the graves stir.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 5, interval: 2.9, startDelay: 0 },
        { enemyType: 'redshroom', count: 8, interval: 2.1, startDelay: 6 },
        { enemyType: 'purplefungi', count: 7, interval: 2.1, startDelay: 20 },
        { enemyType: 'redshroom', count: 7, interval: 2.1, startDelay: 32 },
      ],
    },
    // 19 — the rot spreads.
    {
      groups: [
        { enemyType: 'skullshroom', count: 9, interval: 2.0, startDelay: 0 },
        { enemyType: 'blizzardfungi', count: 8, interval: 2.0, startDelay: 14 },
        { enemyType: 'skullshroom', count: 8, interval: 2.0, startDelay: 27 },
      ],
    },
    // 20 — the graves stir.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 5, interval: 2.7, startDelay: 0 },
        { enemyType: 'glowshroom', count: 9, interval: 1.9, startDelay: 6 },
        { enemyType: 'poisonshroom', count: 8, interval: 1.9, startDelay: 20 },
        { enemyType: 'glowshroom', count: 8, interval: 1.9, startDelay: 33 },
      ],
    },
    // 21 — the rot spreads.
    {
      groups: [
        { enemyType: 'redshroom', count: 9, interval: 1.9, startDelay: 0 },
        { enemyType: 'purplefungi', count: 8, interval: 1.9, startDelay: 14 },
        { enemyType: 'redshroom', count: 8, interval: 1.9, startDelay: 27 },
      ],
    },
    // 22 — the graves stir.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 6, interval: 2.6, startDelay: 0 },
        { enemyType: 'skullshroom', count: 10, interval: 1.8, startDelay: 6 },
        { enemyType: 'blizzardfungi', count: 9, interval: 1.8, startDelay: 20 },
        { enemyType: 'skullshroom', count: 9, interval: 1.8, startDelay: 33 },
      ],
    },
    // 23 — the rot spreads.
    {
      groups: [
        { enemyType: 'glowshroom', count: 10, interval: 1.7, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 9, interval: 1.7, startDelay: 14 },
        { enemyType: 'glowshroom', count: 9, interval: 1.7, startDelay: 27 },
      ],
    },
    // 24 — the graves stir.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 6, interval: 2.5, startDelay: 0 },
        { enemyType: 'redshroom', count: 10, interval: 1.7, startDelay: 6 },
        { enemyType: 'purplefungi', count: 9, interval: 1.7, startDelay: 20 },
        { enemyType: 'redshroom', count: 9, interval: 1.7, startDelay: 33 },
        { enemyType: 'purplefungi', count: 9, interval: 1.7, startDelay: 46 },
      ],
    },
    // 25 — the rot spreads.
    {
      groups: [
        { enemyType: 'skullshroom', count: 11, interval: 1.6, startDelay: 0 },
        { enemyType: 'blizzardfungi', count: 10, interval: 1.6, startDelay: 14 },
        { enemyType: 'skullshroom', count: 10, interval: 1.6, startDelay: 27 },
        { enemyType: 'blizzardfungi', count: 10, interval: 1.6, startDelay: 40 },
      ],
    },
  ],
};

const PATHWAY_OF_THE_FUNGI: LevelDef = {
  id: 119,
  chapter: 2,
  episode: 'IX',
  name: 'Pathway of the Fungi',
  subtitle: 'The Fungi Forest',
  background: 'bg-pathwayofthefungi',
  // Plain grassland with a brown path: a long serpentine, and the five
  // moss-covered golden statues with glowing golden mushrooms beside them
  // between its rows (GOLDEN_STATUES, placed by the game). No new enemy:
  // the prize is the Golden Shroom tower, first win only.
  path: [
    { x: 0, y: 140 },
    { x: 880, y: 140 },
    { x: 880, y: 320 },
    { x: 160, y: 320 },
    { x: 160, y: 500 },
    { x: 1040, y: 500 },
  ],
  plots: [
    { id: 0, pos: { x: 280, y: 230 } },
    { id: 1, pos: { x: 480, y: 230 } },
    { id: 2, pos: { x: 680, y: 230 } },
    { id: 3, pos: { x: 380, y: 410 } },
    { id: 4, pos: { x: 580, y: 410 } },
    { id: 5, pos: { x: 780, y: 410 } },
    { id: 6, pos: { x: 300, y: 600 } },
    { id: 7, pos: { x: 520, y: 600 } },
    { id: 8, pos: { x: 740, y: 600 } },
    { id: 9, pos: { x: 1000, y: 260 } },
    { id: 10, pos: { x: 60, y: 410 } },
    { id: 11, pos: { x: 980, y: 640 } },
  ],
  decor: 'goldstatues',
  startCash: 3400,
  startLives: 5,
  coinReward: 470,
  expReward: 890,
  towerHp: HUB_TOWER_HP,
  waves: [
    // 1 — down the golden path.
    {
      groups: [{ enemyType: 'skullshroom', count: 4, interval: 3.2, startDelay: 0 }],
    },
    // 2 — past the statues.
    {
      groups: [{ enemyType: 'glowshroom', count: 4, interval: 3.1, startDelay: 0 }],
    },
    // 3 — down the golden path.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 1, interval: 3.9, startDelay: 0 },
        { enemyType: 'redshroom', count: 4, interval: 3.1, startDelay: 6 },
      ],
    },
    // 4 — past the statues.
    {
      groups: [{ enemyType: 'skullshroom', count: 4, interval: 3.0, startDelay: 0 }],
    },
    // 5 — down the golden path.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 1, interval: 3.7, startDelay: 0 },
        { enemyType: 'glowshroom', count: 5, interval: 2.9, startDelay: 6 },
      ],
    },
    // 6 — past the statues.
    {
      groups: [{ enemyType: 'redshroom', count: 5, interval: 2.9, startDelay: 0 }],
    },
    // 7 — down the golden path.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 2, interval: 3.6, startDelay: 0 },
        { enemyType: 'skullshroom', count: 5, interval: 2.8, startDelay: 6 },
      ],
    },
    // 8 — past the statues.
    {
      groups: [{ enemyType: 'glowshroom', count: 5, interval: 2.8, startDelay: 0 }],
    },
    // 9 — down the golden path.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 2, interval: 3.5, startDelay: 0 },
        { enemyType: 'redshroom', count: 6, interval: 2.7, startDelay: 6 },
        { enemyType: 'purplefungi', count: 5, interval: 2.7, startDelay: 19 },
      ],
    },
    // 10 — past the statues.
    {
      groups: [
        { enemyType: 'skullshroom', count: 6, interval: 2.6, startDelay: 0 },
        { enemyType: 'blizzardfungi', count: 5, interval: 2.6, startDelay: 13 },
      ],
    },
    // 11 — down the golden path.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 3, interval: 3.4, startDelay: 0 },
        { enemyType: 'glowshroom', count: 6, interval: 2.6, startDelay: 6 },
        { enemyType: 'poisonshroom', count: 5, interval: 2.6, startDelay: 19 },
      ],
    },
    // 12 — past the statues.
    {
      groups: [
        { enemyType: 'redshroom', count: 7, interval: 2.5, startDelay: 0 },
        { enemyType: 'purplefungi', count: 6, interval: 2.5, startDelay: 14 },
      ],
    },
    // 13 — down the golden path.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 3, interval: 3.2, startDelay: 0 },
        { enemyType: 'skullshroom', count: 7, interval: 2.4, startDelay: 6 },
        { enemyType: 'blizzardfungi', count: 6, interval: 2.4, startDelay: 20 },
      ],
    },
    // 14 — past the statues.
    {
      groups: [
        { enemyType: 'glowshroom', count: 7, interval: 2.4, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 6, interval: 2.4, startDelay: 14 },
      ],
    },
    // 15 — down the golden path.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 4, interval: 3.1, startDelay: 0 },
        { enemyType: 'redshroom', count: 7, interval: 2.3, startDelay: 6 },
        { enemyType: 'purplefungi', count: 6, interval: 2.3, startDelay: 19 },
      ],
    },
    // 16 — past the statues.
    {
      groups: [
        { enemyType: 'skullshroom', count: 8, interval: 2.2, startDelay: 0 },
        { enemyType: 'blizzardfungi', count: 7, interval: 2.2, startDelay: 14 },
      ],
    },
    // 17 — down the golden path.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 4, interval: 3.0, startDelay: 0 },
        { enemyType: 'glowshroom', count: 8, interval: 2.2, startDelay: 6 },
        { enemyType: 'poisonshroom', count: 7, interval: 2.2, startDelay: 20 },
        { enemyType: 'glowshroom', count: 7, interval: 2.2, startDelay: 33 },
      ],
    },
    // 18 — past the statues.
    {
      groups: [
        { enemyType: 'redshroom', count: 8, interval: 2.1, startDelay: 0 },
        { enemyType: 'purplefungi', count: 7, interval: 2.1, startDelay: 14 },
        { enemyType: 'redshroom', count: 7, interval: 2.1, startDelay: 26 },
      ],
    },
    // 19 — down the golden path.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 5, interval: 2.8, startDelay: 0 },
        { enemyType: 'skullshroom', count: 9, interval: 2.0, startDelay: 6 },
        { enemyType: 'blizzardfungi', count: 8, interval: 2.0, startDelay: 20 },
        { enemyType: 'skullshroom', count: 8, interval: 2.0, startDelay: 33 },
      ],
    },
    // 20 — past the statues.
    {
      groups: [
        { enemyType: 'glowshroom', count: 9, interval: 2.0, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 8, interval: 2.0, startDelay: 14 },
        { enemyType: 'glowshroom', count: 8, interval: 2.0, startDelay: 27 },
      ],
    },
    // 21 — down the golden path.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 5, interval: 2.7, startDelay: 0 },
        { enemyType: 'redshroom', count: 9, interval: 1.9, startDelay: 6 },
        { enemyType: 'purplefungi', count: 8, interval: 1.9, startDelay: 20 },
        { enemyType: 'redshroom', count: 8, interval: 1.9, startDelay: 33 },
      ],
    },
    // 22 — past the statues.
    {
      groups: [
        { enemyType: 'skullshroom', count: 9, interval: 1.9, startDelay: 0 },
        { enemyType: 'blizzardfungi', count: 8, interval: 1.9, startDelay: 14 },
        { enemyType: 'skullshroom', count: 8, interval: 1.9, startDelay: 27 },
      ],
    },
    // 23 — down the golden path.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 6, interval: 2.6, startDelay: 0 },
        { enemyType: 'glowshroom', count: 10, interval: 1.8, startDelay: 6 },
        { enemyType: 'poisonshroom', count: 9, interval: 1.8, startDelay: 20 },
        { enemyType: 'glowshroom', count: 9, interval: 1.8, startDelay: 33 },
      ],
    },
    // 24 — past the statues.
    {
      groups: [
        { enemyType: 'redshroom', count: 10, interval: 1.7, startDelay: 0 },
        { enemyType: 'purplefungi', count: 9, interval: 1.7, startDelay: 14 },
        { enemyType: 'redshroom', count: 9, interval: 1.7, startDelay: 27 },
      ],
    },
    // 25 — down the golden path.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 6, interval: 2.5, startDelay: 0 },
        { enemyType: 'skullshroom', count: 10, interval: 1.7, startDelay: 6 },
        { enemyType: 'blizzardfungi', count: 9, interval: 1.7, startDelay: 20 },
        { enemyType: 'skullshroom', count: 9, interval: 1.7, startDelay: 33 },
        { enemyType: 'blizzardfungi', count: 9, interval: 1.7, startDelay: 46 },
      ],
    },
    // 26 — past the statues.
    {
      groups: [
        { enemyType: 'glowshroom', count: 11, interval: 1.6, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 10, interval: 1.6, startDelay: 14 },
        { enemyType: 'glowshroom', count: 10, interval: 1.6, startDelay: 27 },
        { enemyType: 'poisonshroom', count: 10, interval: 1.6, startDelay: 40 },
      ],
    },
  ],
};

const MAGICAL_MAYHEM: LevelDef = {
  id: 120,
  chapter: 2,
  episode: 'X',
  name: 'Magical Mayhem',
  subtitle: 'The Fungi Forest',
  background: 'bg-magicalmayhem',
  // Giant redwoods with blue and orange glowing mushrooms: four long
  // switchbacks between the trees, glowing clusters beside them
  // (GLOW_CLUSTERS). The Hyno-Fungi comes EXACTLY three times — waves 8, 17
  // and 25 — and hypnotises 1–2 towers every 5 s while it lives.
  path: [
    { x: 0, y: 600 },
    { x: 200, y: 600 },
    { x: 200, y: 140 },
    { x: 420, y: 140 },
    { x: 420, y: 600 },
    { x: 640, y: 600 },
    { x: 640, y: 140 },
    { x: 860, y: 140 },
    { x: 860, y: 420 },
    { x: 1040, y: 420 },
  ],
  plots: [
    { id: 0, pos: { x: 310, y: 260 } },
    { id: 1, pos: { x: 310, y: 380 } },
    { id: 2, pos: { x: 310, y: 500 } },
    { id: 3, pos: { x: 530, y: 240 } },
    { id: 4, pos: { x: 530, y: 360 } },
    { id: 5, pos: { x: 530, y: 480 } },
    { id: 6, pos: { x: 750, y: 260 } },
    { id: 7, pos: { x: 750, y: 380 } },
    { id: 8, pos: { x: 750, y: 520 } },
    { id: 9, pos: { x: 100, y: 400 } },
    { id: 10, pos: { x: 960, y: 280 } },
    { id: 11, pos: { x: 960, y: 560 } },
  ],
  decor: 'glowclusters',
  startCash: 3500,
  startLives: 5,
  coinReward: 480,
  expReward: 910,
  towerHp: HUB_TOWER_HP,
  waves: [
    // 1 — blue and orange glow.
    {
      groups: [{ enemyType: 'skullshroom', count: 4, interval: 3.2, startDelay: 0 }],
    },
    // 2 — the redwoods hum.
    {
      groups: [{ enemyType: 'glowshroom', count: 4, interval: 3.1, startDelay: 0 }],
    },
    // 3 — blue and orange glow.
    {
      groups: [{ enemyType: 'redshroom', count: 4, interval: 3.1, startDelay: 0 }],
    },
    // 4 — the redwoods hum.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 1, interval: 3.8, startDelay: 0 },
        { enemyType: 'skullshroom', count: 4, interval: 3.0, startDelay: 6 },
      ],
    },
    // 5 — blue and orange glow.
    {
      groups: [{ enemyType: 'glowshroom', count: 5, interval: 3.0, startDelay: 0 }],
    },
    // 6 — the redwoods hum.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 2, interval: 3.7, startDelay: 0 },
        { enemyType: 'redshroom', count: 5, interval: 2.9, startDelay: 6 },
      ],
    },
    // 7 — blue and orange glow.
    {
      groups: [{ enemyType: 'skullshroom', count: 5, interval: 2.8, startDelay: 0 }],
    },
    // 8 — a Hyno-Fungi glows blue.
    {
      groups: [
        { enemyType: 'hynofungi', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'zombieshroom', count: 2, interval: 3.6, startDelay: 8 },
        { enemyType: 'glowshroom', count: 5, interval: 2.8, startDelay: 14 },
      ],
    },
    // 9 — blue and orange glow.
    {
      groups: [{ enemyType: 'redshroom', count: 6, interval: 2.7, startDelay: 0 }],
    },
    // 10 — the redwoods hum.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 3, interval: 3.5, startDelay: 0 },
        { enemyType: 'skullshroom', count: 6, interval: 2.7, startDelay: 6 },
        { enemyType: 'blizzardfungi', count: 5, interval: 2.7, startDelay: 19 },
      ],
    },
    // 11 — blue and orange glow.
    {
      groups: [
        { enemyType: 'glowshroom', count: 6, interval: 2.6, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 5, interval: 2.6, startDelay: 13 },
      ],
    },
    // 12 — the redwoods hum.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 3, interval: 3.3, startDelay: 0 },
        { enemyType: 'redshroom', count: 6, interval: 2.5, startDelay: 6 },
        { enemyType: 'purplefungi', count: 5, interval: 2.5, startDelay: 19 },
      ],
    },
    // 13 — blue and orange glow.
    {
      groups: [
        { enemyType: 'skullshroom', count: 7, interval: 2.5, startDelay: 0 },
        { enemyType: 'blizzardfungi', count: 6, interval: 2.5, startDelay: 14 },
      ],
    },
    // 14 — the redwoods hum.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 3, interval: 3.2, startDelay: 0 },
        { enemyType: 'glowshroom', count: 7, interval: 2.4, startDelay: 6 },
        { enemyType: 'poisonshroom', count: 6, interval: 2.4, startDelay: 20 },
      ],
    },
    // 15 — blue and orange glow.
    {
      groups: [
        { enemyType: 'redshroom', count: 7, interval: 2.4, startDelay: 0 },
        { enemyType: 'purplefungi', count: 6, interval: 2.4, startDelay: 14 },
      ],
    },
    // 16 — the redwoods hum.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 4, interval: 3.1, startDelay: 0 },
        { enemyType: 'skullshroom', count: 7, interval: 2.3, startDelay: 6 },
        { enemyType: 'blizzardfungi', count: 6, interval: 2.3, startDelay: 19 },
      ],
    },
    // 17 — a Hyno-Fungi glows blue.
    {
      groups: [
        { enemyType: 'hynofungi', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'glowshroom', count: 8, interval: 2.3, startDelay: 8 },
        { enemyType: 'poisonshroom', count: 7, interval: 2.3, startDelay: 23 },
      ],
    },
    // 18 — the redwoods hum.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 4, interval: 3.0, startDelay: 0 },
        { enemyType: 'redshroom', count: 8, interval: 2.2, startDelay: 6 },
        { enemyType: 'purplefungi', count: 7, interval: 2.2, startDelay: 20 },
        { enemyType: 'redshroom', count: 7, interval: 2.2, startDelay: 33 },
      ],
    },
    // 19 — blue and orange glow.
    {
      groups: [
        { enemyType: 'skullshroom', count: 8, interval: 2.1, startDelay: 0 },
        { enemyType: 'blizzardfungi', count: 7, interval: 2.1, startDelay: 14 },
        { enemyType: 'skullshroom', count: 7, interval: 2.1, startDelay: 26 },
      ],
    },
    // 20 — the redwoods hum.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 5, interval: 2.9, startDelay: 0 },
        { enemyType: 'glowshroom', count: 8, interval: 2.1, startDelay: 6 },
        { enemyType: 'poisonshroom', count: 7, interval: 2.1, startDelay: 20 },
        { enemyType: 'glowshroom', count: 7, interval: 2.1, startDelay: 32 },
      ],
    },
    // 21 — blue and orange glow.
    {
      groups: [
        { enemyType: 'redshroom', count: 9, interval: 2.0, startDelay: 0 },
        { enemyType: 'purplefungi', count: 8, interval: 2.0, startDelay: 14 },
        { enemyType: 'redshroom', count: 8, interval: 2.0, startDelay: 27 },
      ],
    },
    // 22 — the redwoods hum.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 5, interval: 2.8, startDelay: 0 },
        { enemyType: 'skullshroom', count: 9, interval: 2.0, startDelay: 6 },
        { enemyType: 'blizzardfungi', count: 8, interval: 2.0, startDelay: 20 },
        { enemyType: 'skullshroom', count: 8, interval: 2.0, startDelay: 33 },
      ],
    },
    // 23 — blue and orange glow.
    {
      groups: [
        { enemyType: 'glowshroom', count: 9, interval: 1.9, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 8, interval: 1.9, startDelay: 14 },
        { enemyType: 'glowshroom', count: 8, interval: 1.9, startDelay: 27 },
      ],
    },
    // 24 — the redwoods hum.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 6, interval: 2.6, startDelay: 0 },
        { enemyType: 'redshroom', count: 9, interval: 1.8, startDelay: 6 },
        { enemyType: 'purplefungi', count: 8, interval: 1.8, startDelay: 19 },
        { enemyType: 'redshroom', count: 8, interval: 1.8, startDelay: 31 },
      ],
    },
    // 25 — a Hyno-Fungi glows blue.
    {
      groups: [
        { enemyType: 'hynofungi', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'skullshroom', count: 10, interval: 1.8, startDelay: 8 },
        { enemyType: 'blizzardfungi', count: 9, interval: 1.8, startDelay: 22 },
        { enemyType: 'skullshroom', count: 9, interval: 1.8, startDelay: 35 },
      ],
    },
    // 26 — the redwoods hum.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 6, interval: 2.5, startDelay: 0 },
        { enemyType: 'glowshroom', count: 10, interval: 1.7, startDelay: 6 },
        { enemyType: 'poisonshroom', count: 9, interval: 1.7, startDelay: 20 },
        { enemyType: 'glowshroom', count: 9, interval: 1.7, startDelay: 33 },
      ],
    },
    // 27 — blue and orange glow.
    {
      groups: [
        { enemyType: 'redshroom', count: 10, interval: 1.7, startDelay: 0 },
        { enemyType: 'purplefungi', count: 9, interval: 1.7, startDelay: 14 },
        { enemyType: 'redshroom', count: 9, interval: 1.7, startDelay: 27 },
        { enemyType: 'purplefungi', count: 9, interval: 1.7, startDelay: 40 },
      ],
    },
    // 28 — the redwoods hum.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 7, interval: 2.4, startDelay: 0 },
        { enemyType: 'skullshroom', count: 11, interval: 1.6, startDelay: 6 },
        { enemyType: 'blizzardfungi', count: 10, interval: 1.6, startDelay: 20 },
        { enemyType: 'skullshroom', count: 10, interval: 1.6, startDelay: 33 },
        { enemyType: 'blizzardfungi', count: 10, interval: 1.6, startDelay: 46 },
      ],
    },
  ],
};

const THE_SHROOMBELLION: LevelDef = {
  id: 121,
  chapter: 2,
  episode: 'XI',
  name: 'The Shroombellion',
  subtitle: 'The Fungi Forest',
  background: 'bg-shroombellion',
  // The finale. A forest of huge trees: one giant tree house in the middle,
  // three smaller ones round it, rope bridges between them (TREE_HOUSES,
  // placed by the game). The track is the chapter's longest by far — three
  // tight switchbacks up the left, across the top over the tree houses, and
  // three down the right (~4340 px) — because King Shroometron walks it
  // ALONE on wave 30 with 160 000 HP at speed 60, and the brief demands he be
  // beatable with no potion or item (tests/sim/fungiFinale.test.ts).
  path: [
    { x: 0, y: 640 },
    { x: 80, y: 640 },
    { x: 80, y: 90 },
    { x: 200, y: 90 },
    { x: 200, y: 640 },
    { x: 320, y: 640 },
    { x: 320, y: 90 },
    { x: 720, y: 90 },
    { x: 720, y: 640 },
    { x: 840, y: 640 },
    { x: 840, y: 90 },
    { x: 960, y: 90 },
    { x: 960, y: 640 },
    { x: 1040, y: 640 },
  ],
  plots: [
    { id: 0, pos: { x: 140, y: 170 } },
    { id: 1, pos: { x: 140, y: 290 } },
    { id: 2, pos: { x: 140, y: 410 } },
    { id: 3, pos: { x: 140, y: 530 } },
    { id: 4, pos: { x: 260, y: 170 } },
    { id: 5, pos: { x: 260, y: 290 } },
    { id: 6, pos: { x: 260, y: 410 } },
    { id: 7, pos: { x: 260, y: 530 } },
    { id: 8, pos: { x: 780, y: 170 } },
    { id: 9, pos: { x: 780, y: 290 } },
    { id: 10, pos: { x: 780, y: 410 } },
    { id: 11, pos: { x: 780, y: 530 } },
    { id: 12, pos: { x: 900, y: 170 } },
    { id: 13, pos: { x: 900, y: 290 } },
    { id: 14, pos: { x: 900, y: 410 } },
    { id: 15, pos: { x: 900, y: 530 } },
    { id: 16, pos: { x: 400, y: 500 } },
    { id: 17, pos: { x: 640, y: 500 } },
  ],
  decor: 'treehouses',
  startCash: 4000,
  startLives: 5,
  coinReward: 600,
  expReward: 1200,
  towerHp: HUB_TOWER_HP,
  waves: [
    // 1 — the rebellion marches.
    {
      groups: [{ enemyType: 'skullshroom', count: 4, interval: 3.2, startDelay: 0 }],
    },
    // 2 — down from the tree houses.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 1, interval: 3.9, startDelay: 0 },
        { enemyType: 'glowshroom', count: 4, interval: 3.1, startDelay: 6 },
      ],
    },
    // 3 — the rebellion marches.
    {
      groups: [{ enemyType: 'redshroom', count: 4, interval: 3.1, startDelay: 0 }],
    },
    // 4 — down from the tree houses.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 1, interval: 3.8, startDelay: 0 },
        { enemyType: 'skullshroom', count: 4, interval: 3.0, startDelay: 6 },
      ],
    },
    // 5 — the rebellion marches.
    {
      groups: [{ enemyType: 'glowshroom', count: 4, interval: 3.0, startDelay: 0 }],
    },
    // 6 — down from the tree houses.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 2, interval: 3.7, startDelay: 0 },
        { enemyType: 'redshroom', count: 5, interval: 2.9, startDelay: 6 },
      ],
    },
    // 7 — the rebellion marches.
    {
      groups: [{ enemyType: 'skullshroom', count: 5, interval: 2.9, startDelay: 0 }],
    },
    // 8 — down from the tree houses.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 2, interval: 3.6, startDelay: 0 },
        { enemyType: 'glowshroom', count: 5, interval: 2.8, startDelay: 6 },
      ],
    },
    // 9 — the rebellion marches.
    {
      groups: [{ enemyType: 'redshroom', count: 5, interval: 2.8, startDelay: 0 }],
    },
    // 10 — down from the tree houses.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 2, interval: 3.5, startDelay: 0 },
        { enemyType: 'skullshroom', count: 6, interval: 2.7, startDelay: 6 },
      ],
    },
    // 11 — the rebellion marches.
    {
      groups: [
        { enemyType: 'glowshroom', count: 6, interval: 2.6, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 5, interval: 2.6, startDelay: 13 },
      ],
    },
    // 12 — down from the tree houses.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 3, interval: 3.4, startDelay: 0 },
        { enemyType: 'redshroom', count: 6, interval: 2.6, startDelay: 6 },
        { enemyType: 'purplefungi', count: 5, interval: 2.6, startDelay: 19 },
      ],
    },
    // 13 — the rebellion marches.
    {
      groups: [
        { enemyType: 'skullshroom', count: 6, interval: 2.5, startDelay: 0 },
        { enemyType: 'blizzardfungi', count: 5, interval: 2.5, startDelay: 13 },
      ],
    },
    // 14 — down from the tree houses.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 3, interval: 3.3, startDelay: 0 },
        { enemyType: 'glowshroom', count: 7, interval: 2.5, startDelay: 6 },
        { enemyType: 'poisonshroom', count: 6, interval: 2.5, startDelay: 20 },
      ],
    },
    // 15 — the rebellion marches.
    {
      groups: [
        { enemyType: 'redshroom', count: 7, interval: 2.4, startDelay: 0 },
        { enemyType: 'purplefungi', count: 6, interval: 2.4, startDelay: 14 },
      ],
    },
    // 16 — down from the tree houses.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 4, interval: 3.2, startDelay: 0 },
        { enemyType: 'skullshroom', count: 7, interval: 2.4, startDelay: 6 },
        { enemyType: 'blizzardfungi', count: 6, interval: 2.4, startDelay: 20 },
      ],
    },
    // 17 — the rebellion marches.
    {
      groups: [
        { enemyType: 'glowshroom', count: 7, interval: 2.3, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 6, interval: 2.3, startDelay: 13 },
      ],
    },
    // 18 — down from the tree houses.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 4, interval: 3.1, startDelay: 0 },
        { enemyType: 'redshroom', count: 8, interval: 2.3, startDelay: 6 },
        { enemyType: 'purplefungi', count: 7, interval: 2.3, startDelay: 21 },
      ],
    },
    // 19 — the rebellion marches.
    {
      groups: [
        { enemyType: 'skullshroom', count: 8, interval: 2.2, startDelay: 0 },
        { enemyType: 'blizzardfungi', count: 7, interval: 2.2, startDelay: 14 },
      ],
    },
    // 20 — down from the tree houses.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 4, interval: 3.0, startDelay: 0 },
        { enemyType: 'glowshroom', count: 8, interval: 2.2, startDelay: 6 },
        { enemyType: 'poisonshroom', count: 7, interval: 2.2, startDelay: 20 },
        { enemyType: 'glowshroom', count: 7, interval: 2.2, startDelay: 33 },
      ],
    },
    // 21 — the rebellion marches.
    {
      groups: [
        { enemyType: 'redshroom', count: 8, interval: 2.1, startDelay: 0 },
        { enemyType: 'purplefungi', count: 7, interval: 2.1, startDelay: 14 },
        { enemyType: 'redshroom', count: 7, interval: 2.1, startDelay: 26 },
      ],
    },
    // 22 — down from the tree houses.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 5, interval: 2.8, startDelay: 0 },
        { enemyType: 'skullshroom', count: 9, interval: 2.0, startDelay: 6 },
        { enemyType: 'blizzardfungi', count: 8, interval: 2.0, startDelay: 20 },
        { enemyType: 'skullshroom', count: 8, interval: 2.0, startDelay: 33 },
      ],
    },
    // 23 — the rebellion marches.
    {
      groups: [
        { enemyType: 'glowshroom', count: 9, interval: 2.0, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 8, interval: 2.0, startDelay: 14 },
        { enemyType: 'glowshroom', count: 8, interval: 2.0, startDelay: 27 },
      ],
    },
    // 24 — down from the tree houses.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 5, interval: 2.7, startDelay: 0 },
        { enemyType: 'redshroom', count: 9, interval: 1.9, startDelay: 6 },
        { enemyType: 'purplefungi', count: 8, interval: 1.9, startDelay: 20 },
        { enemyType: 'redshroom', count: 8, interval: 1.9, startDelay: 33 },
      ],
    },
    // 25 — the rebellion marches.
    {
      groups: [
        { enemyType: 'skullshroom', count: 9, interval: 1.9, startDelay: 0 },
        { enemyType: 'blizzardfungi', count: 8, interval: 1.9, startDelay: 14 },
        { enemyType: 'skullshroom', count: 8, interval: 1.9, startDelay: 27 },
      ],
    },
    // 26 — down from the tree houses.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 6, interval: 2.6, startDelay: 0 },
        { enemyType: 'glowshroom', count: 10, interval: 1.8, startDelay: 6 },
        { enemyType: 'poisonshroom', count: 9, interval: 1.8, startDelay: 20 },
        { enemyType: 'glowshroom', count: 9, interval: 1.8, startDelay: 33 },
      ],
    },
    // 27 — the rebellion marches.
    {
      groups: [
        { enemyType: 'redshroom', count: 10, interval: 1.8, startDelay: 0 },
        { enemyType: 'purplefungi', count: 9, interval: 1.8, startDelay: 14 },
        { enemyType: 'redshroom', count: 9, interval: 1.8, startDelay: 27 },
      ],
    },
    // 28 — down from the tree houses.
    {
      groups: [
        { enemyType: 'zombieshroom', count: 6, interval: 2.5, startDelay: 0 },
        { enemyType: 'skullshroom', count: 10, interval: 1.7, startDelay: 6 },
        { enemyType: 'blizzardfungi', count: 9, interval: 1.7, startDelay: 20 },
        { enemyType: 'skullshroom', count: 9, interval: 1.7, startDelay: 33 },
      ],
    },
    // 29 — the rebellion marches.
    {
      groups: [
        { enemyType: 'glowshroom', count: 10, interval: 1.7, startDelay: 0 },
        { enemyType: 'poisonshroom', count: 9, interval: 1.7, startDelay: 14 },
        { enemyType: 'glowshroom', count: 9, interval: 1.7, startDelay: 27 },
        { enemyType: 'poisonshroom', count: 9, interval: 1.7, startDelay: 40 },
      ],
    },
    // 30 — KING SHROOMETRON — alone.
    {
      groups: [{ enemyType: 'kingshroometron', count: 1, interval: 1, startDelay: 0 }],
    },
  ],
};

export const HUB_CHAPTERS: HubChapterDef[] = [
  {
    number: 1,
    name: 'The Moon',
    art: 'hub-moon',
    color: 0xc0392b,
    colorHex: '#e8674f',
    testHex: '#8a1a1a',
    arenaId: MOON_ARENA_LEVEL_ID,
    levels: [
      HAMBI_BAR,
      HTD_EXHIBITION,
      COSMIC_QUARTERS,
      GAZING_DOME,
      SPACE_STATION,
      CONSTELLATION_VALLEY,
      BUN_BEACH,
      NACHYO_BUSINESS,
      ROCKET_RAGE,
      THROUGH_SPACE_AND_TIME,
    ],
  },
  {
    number: 2,
    name: 'The Fungi Forest',
    art: 'hub-fungi',
    color: 0xe67e22,
    colorHex: '#f39c4a',
    testHex: '#a3500f',
    arenaId: FUNGI_ARENA_LEVEL_ID,
    levels: [
      MUSHROOM_MANGROVE,
      VILLAGE_OF_VEG,
      FUNGI_FORESTRY,
      POISON_TURF,
      MUSHY_MAYHEM,
      GLOWING_FOREST,
      ICY_PHOTOSYNTHESIS,
      GRAVE_OF_THE_ROTTEN,
      PATHWAY_OF_THE_FUNGI,
      MAGICAL_MAYHEM,
      THE_SHROOMBELLION,
    ],
  },
];

/** The Moon's own ten levels (chapter 01), for the checks that mean only it. */
export const MOON_LEVELS: LevelDef[] = HUB_CHAPTERS[0].levels;

/** The hub chapter a level belongs to (undefined off the Hub). */
export function hubChapterOf(id: number): HubChapterDef | undefined {
  return HUB_CHAPTERS.find((c) => c.levels.some((l) => l.id === id));
}

/** Every hub level, in play order (ids ascending). */
export const HUB_LEVELS: LevelDef[] = HUB_CHAPTERS.flatMap((c) => c.levels);

/**
 * The hub level whose first win awards the Diamond — HTD Exhibition. Pinned
 * by id, not "the last level": the chapter grew past it in Revision 4 and
 * the prize stays where the spec put it.
 */
export const DIAMOND_LEVEL_ID = HTD_EXHIBITION.id;

/**
 * The hub level whose EVERY win pays a piece of Space Debris — Rocket Rage.
 * Unlike the Diamond this is not once-ever: the brief is "every time you win
 * you get space debris", so a replay pays again (Normal track only).
 */
export const DEBRIS_LEVEL_ID = ROCKET_RAGE.id;

/**
 * The Fungi Forest's finale (spec 2026-10-27): every real win plays King
 * Shroometron's send-off, and the FIRST pays the Token of Shroom.
 */
export const SHROOMBELLION_LEVEL_ID = THE_SHROOMBELLION.id;

/**
 * The hub level whose FIRST win awards the Moon Stone — the chapter finale.
 * Pinned by id, like the Diamond: "the last level" would move if the chapter
 * grows again, and the brief says the stone is this level's prize.
 */
export const MOONSTONE_LEVEL_ID = THROUGH_SPACE_AND_TIME.id;

export function isHubLevelId(id: number): boolean {
  return HUB_LEVELS.some((l) => l.id === id);
}

export function findHubLevel(id: number): LevelDef | undefined {
  return HUB_LEVELS.find((l) => l.id === id);
}

/**
 * 1-based position of a hub level in play order — the number `hambi.hub`
 * stores once it is won. Hambi Bar is 1, HTD Exhibition is 2.
 */
export function hubLevelNumber(id: number): number {
  return HUB_LEVELS.findIndex((l) => l.id === id) + 1;
}

/** Whether a hub level may be played: sequential, so it needs the one before it won. */
export function isHubLevelUnlocked(id: number, hubCompleted: number): boolean {
  const n = hubLevelNumber(id);
  return n >= 1 && hubCompleted >= n - 1;
}
